import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft, FileText, StickyNote, Image, FolderOpen, Plus, Trash2, 
  Phone, Mail, Building, Download, Calendar, Save, X, Send,
  Upload, User, Users, Shield, DollarSign, Search, Home, Ruler, Star, MessageSquareQuote,
  Receipt, ClipboardList, CreditCard, CheckCircle, Edit2, FileDown, Printer, Copy,
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, GripVertical, UserPlus, Sparkles, Mic, MicOff, Camera
} from 'lucide-react';
import CompanyLogo from '../components/CompanyLogo';
import LanguageToggle from '../components/LanguageToggle';
import SmartPricingSuggestions from '../components/SmartPricingSuggestions';
import LeadInvoiceTab from '../components/lead/tabs/LeadInvoiceTab';
import LeadChangeOrderTab from '../components/lead/tabs/LeadChangeOrderTab';
import EstimateLineItemsSection from '../components/lead/tabs/EstimateLineItemsSection';
import EstimateLumpSumSection from '../components/lead/tabs/EstimateLumpSumSection';
import EstimateDocumentSections from '../components/lead/tabs/EstimateDocumentSections';
import LeadNotesTab from '../components/lead/tabs/LeadNotesTab';
import LeadFilesTab from '../components/lead/tabs/LeadFilesTab';
import LeadContactsTab from '../components/lead/tabs/LeadContactsTab';
import LeadProjectTab from '../components/lead/tabs/LeadProjectTab';
import EmailWriterPanel from '../components/lead/EmailWriterPanel';
import InsuranceInfoSection from '../components/lead/InsuranceInfoSection';
import ProjectMeasurementsCard from '../components/lead/ProjectMeasurementsCard';
import ContactInformationCard from '../components/lead/ContactInformationCard';
import QuickActionsCard from '../components/lead/QuickActionsCard';
import EstimateTypeSelectorCard from '../components/lead/EstimateTypeSelectorCard';
import LeadDetailHeader from '../components/lead/LeadDetailHeader';
import LeadTabNavigation from '../components/lead/LeadTabNavigation';
import SaveTemplateModal from '../components/lead/SaveTemplateModal';
import AIEstimateGeneratorCard from '../components/lead/AIEstimateGeneratorCard';
import ProposalPreviewCard from '../components/lead/ProposalPreviewCard';
import { useLanguage } from '../i18n/LanguageContext';
import { useSettings } from '../contexts/SettingsContext';
import { COMPANY, DEFAULT_TERMS } from '../config/company';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import Fuse from 'fuse.js';
import { Document, Packer, Paragraph, Table, TableRow, TableCell, TextRun, HeadingLevel, AlignmentType, WidthType, BorderStyle, PageBreak, Header, Footer, PageNumber, NumberFormat } from 'docx';

// Helper function to safely get photo path (handles both string and object formats)
const getPhotoPath = (photo) => {
  if (!photo) return '';
  if (typeof photo === 'string') return photo;
  if (typeof photo === 'object') return photo.path || photo.url || photo.id || '';
  return '';
};

// Helper function to get photo filename for display
const getPhotoFilename = (photo) => {
  const path = getPhotoPath(photo);
  if (!path) return 'Unknown';
  const parts = path.split('/');
  const filename = parts[parts.length - 1] || parts[0] || 'Unknown';
  return filename.replace(/^[a-f0-9-]+_/, '');
};

// Default Introduction Letter
const DEFAULT_INTRO_LETTER = `At JY Mega FC Construction Inc., we believe a roof replacement estimate is more than just a list of numbers—it's your roadmap to a safer, more resilient home in Florida's challenging climate. As a family-owned, fully licensed (CGC1536790), insured, and certified general contractor with over 20 years of experience, we're committed to turning potential vulnerabilities into strong, enduring solutions. This document isn't merely a quote; it's our promise of quality craftsmanship, code compliance, and personalized service tailored to protect your family and investment against hurricanes, heavy rains, and relentless sun.

Florida's unique environment demands expertise. From Hurricane Ian's aftermath to everyday wind events, roofs here must meet strict Florida Building Code (FBC) standards for high-velocity hurricane zones (HVHZ) in areas like Broward and Collier Counties. Our estimates incorporate these essentials: premium materials like impact-resistant TPO, modified bitumen, or tile systems; enhanced wind-uplift mitigation; and energy-efficient insulation to qualify for insurance discounts via wind mitigation credits. By choosing us, you're not just replacing a roof—you're enhancing your home's value, reducing energy bills, and gaining eligibility for up to 50% savings on windstorm premiums through proper documentation and inspections.

What sets JY Mega FC Construction apart? We're not a fly-by-night operation; we're a trusted team led by Jhony Alexander Ochoa, serving Naples, Fort Lauderdale, Tamarac, and beyond. Our 5-star reviews on Yelp, Angi, and Houzz speak volumes: "Outstanding workmanship... amazed by the results," says one client after a full remodel. We've handled everything from home additions and kitchen renovations to impact windows and post-storm repairs, ensuring every project is on-time, clean, and budget-conscious. We offer free consultations, transparent pricing with no hidden fees, and a satisfaction guarantee—because your peace of mind is our mission.

This estimate details every line item, from tear-off to final certification, using industry-standard tools like Xactimate for accuracy. It's designed to empower you: Compare it confidently, knowing it reflects superior quality, creative solutions, and a commitment to turning your dreams into reality. Don't settle for less in a state where storms don't compromise—choose JY Mega FC Construction for a roof that stands the test of time.

Ready to proceed? Contact us at 954-339-3278 or jymegaconstruction@gmail.com for your free site assessment. Let's build a stronger future together.

Sincerely,

Johny Pichardo,
CEO
JYMegaFcConstruction, Inc.`;

// Default Reviews Content
const DEFAULT_REVIEWS = `WHAT OUR CLIENTS SAY ABOUT US

⭐⭐⭐⭐⭐ From Angi (Church Project, including new roof):
"JY Mega Construction completed multiple projects at our church. They put on a new roof, repaired and painted our very old building and rebuilt the cross that had started to fall off of the steeple and had to be removed. Throughout each project, Jhony kept in contact with me the entire time."
— Satisfied Church Client, Naples Area

⭐⭐⭐⭐⭐ From Angi (General Excellence & Communication):
"We have used JY Mega for several projects in the last few months including a kitchen remodel. We were very impressed by the quality of their work. The workmanship was outstanding and we were amazed by the results. The entire team was great. They were knowledgeable, on time, and always cleaned up when they were done."
— Homeowner, Multiple Projects

⭐⭐⭐⭐⭐ From Angi (Ongoing Responsiveness):
"Jhonny and JY Mega Construction remained in continued communication, and provided stellar results for our project... Even after the work had completed they continued to remain in touch, and were immediately responsive to any requests no matter how large or small."
— Client on Outdoor/Remodel Project

⭐⭐⭐⭐⭐ From Yelp (Prompt & Competitive Service):
"We recently had JY Mega FC Construction install full impact windows/doors/garage door on our new home. From the beginning of the process Jhony and the team were prompt, thorough and extremely competitive with pricing. We couldn't be happier with the job they did."
— New Home Client

⭐⭐⭐⭐⭐ From HomeAdvisor (Knowledge & Reassurance):
"My home had water damage upstairs and downstairs. From the very beginning JY Mega FC Construction came by and I felt very reassured that they had very good knowledge of their work."
— Water Damage Repair Client

📊 Overall Ratings Summary:
• Angi: 4.7 out of 5 stars (based on 100+ reviews)
• Yelp: 5 stars (from verified clients)
• BuildZoom Score: 114 (above 98% of Florida contractors)
• Trusted for roofing, remodels, and storm-related work in South Florida`;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const LeadDetail = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { settings } = useSettings();
  const estimateRef = useRef(null);
  const invoiceRef = useRef(null);
  const changeOrderRef = useRef(null);
  
  const [lead, setLead] = useState(null);
  const [allLeads, setAllLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [projectTypes, setProjectTypes] = useState([]);
  const [lineItemTemplates, setLineItemTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showTemplateSearch, setShowTemplateSearch] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newInsuranceNote, setNewInsuranceNote] = useState('');
  const [newProjectNote, setNewProjectNote] = useState('');
  const [editingInsuranceNote, setEditingInsuranceNote] = useState(null);
  const [editingProjectNote, setEditingProjectNote] = useState(null);
  const [editInsuranceNoteText, setEditInsuranceNoteText] = useState('');
  const [editProjectNoteText, setEditProjectNoteText] = useState('');
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  // Lump Sum state
  const [lumpSumTemplates, setLumpSumTemplates] = useState([]);
  const [lumpSumSearchQuery, setLumpSumSearchQuery] = useState('');
  const [showLumpSumLibrary, setShowLumpSumLibrary] = useState(false);
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);
  
  // Estimate Variants state (multiple estimates per lead by project type)
  const [currentVariantIndex, setCurrentVariantIndex] = useState(0);
  const [showCreateVariant, setShowCreateVariant] = useState(false);
  const [newVariantType, setNewVariantType] = useState('');

  // Contacts state
  const [contacts, setContacts] = useState([]);
  const [showContactModal, setShowContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({
    name: '', role: 'homeowner', company: '', email: '', phone: '', mobile: '', notes: ''
  });

  // AI Estimate Generator state
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiProjectCategory, setAiProjectCategory] = useState('');
  const [aiQualityTier, setAiQualityTier] = useState('');
  const [aiRoofType, setAiRoofType] = useState('');
  const [aiRegion, setAiRegion] = useState('collier');
  const [aiEstimateType, setAiEstimateType] = useState('lump_sum'); // 'lump_sum' or 'itemized'
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiGeneratedEstimate, setAiGeneratedEstimate] = useState(null);
  const [aiPricingData, setAiPricingData] = useState([]);
  const [aiCalculatedCost, setAiCalculatedCost] = useState(null);
  const [aiItemizedEstimate, setAiItemizedEstimate] = useState([]); // For editable itemized line items
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  // Photo Analysis state
  const [photoAnalyzing, setPhotoAnalyzing] = useState(false);
  const [photoAnalysis, setPhotoAnalysis] = useState(null);
  const [photoReferenceObject, setPhotoReferenceObject] = useState('');
  const [photoReferenceMeasurement, setPhotoReferenceMeasurement] = useState('');
  const [showPhotoMeasureModal, setShowPhotoMeasureModal] = useState(false);
  const [pendingPhotoFile, setPendingPhotoFile] = useState(null);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  
  // Email Writer state
  const [showEmailWriter, setShowEmailWriter] = useState(false);
  const [emailGenerating, setEmailGenerating] = useState(false);
  const [generatedEmail, setGeneratedEmail] = useState(null);
  const [emailType, setEmailType] = useState('follow_up');
  const [customEmailContext, setCustomEmailContext] = useState('');
  
  // Measurement fields for AI estimate
  const [aiMeasurements, setAiMeasurements] = useState({
    // Roofing
    roof_sqft: '',
    roof_stories: '1',
    roof_pitch: 'standard',
    roof_squares: '',
    roof_circumference: '',
    roof_ridge: '',
    roof_valleys: '',
    roof_eaves: '',
    roof_rakes: '',
    
    // Kitchen/Bathroom Remodel
    kitchen_sqft: '',
    bathroom_sqft: '',
    countertop_lf: '',
    cabinet_lf: '',
    tiling_lf: '',
    layout_complexity: 'moderate',
    finish_level: 'standard',
    num_bathrooms: '1',
    
    // Flooring
    flooring_sqft: '',
    baseboard_lf: '',
    subfloor_condition: 'good',
    flooring_type: 'lvp',
    
    // Painting
    paint_sqft: '',
    paint_coats: '2',
    trim_lf: '',
    wall_height: 'standard',
    surface_condition: 'good',
    
    // Pool Construction
    pool_volume: '',
    pool_surface_sqft: '',
    deck_sqft: '',
    pool_perimeter_lf: '',
    pool_equipment: 'standard',
    soil_type: 'normal',
    pool_length: '',
    pool_width: '',
    pool_depth: '',
    pool_type: 'gunite',
    
    // Drain Line Repair
    drain_pipe_lf: '',
    pipe_diameter: '4',
    pipe_depth: '',
    access_points: '1',
    pipe_material: 'pvc',
    
    // HVAC Replacement
    hvac_tonnage: '',
    indoor_units: '1',
    outdoor_units: '1',
    refrigerant_line_lf: '',
    ductwork_modification: 'no',
    
    // Mold Remediation
    mold_sqft: '',
    containment_sqft: '',
    affected_material: 'drywall',
    water_category: '1',
    air_testing: 'no',
    
    // Drywall Repair
    drywall_sqft: '',
    seams_lf: '',
    finish_level_drywall: '4',
    
    // Fence Installation
    fence_lf: '',
    fence_height: '6',
    fence_posts: '',
    fence_gates: '1',
    fence_material: 'wood',
    
    // General
    total_sqft: '',
    num_rooms: '',
  });

  // Contact roles
  const CONTACT_ROLES = {
    homeowner: { label: 'Homeowner', labelEs: 'Propietario', color: '#3b82f6' },
    project_manager: { label: 'Project Manager', labelEs: 'Gerente de Proyecto', color: '#8b5cf6' },
    architect: { label: 'Architect', labelEs: 'Arquitecto', color: '#6366f1' },
    engineer: { label: 'Engineer', labelEs: 'Ingeniero', color: '#14b8a6' },
    inspector: { label: 'Inspector', labelEs: 'Inspector', color: '#f59e0b' },
    hoa_rep: { label: 'HOA Representative', labelEs: 'Representante HOA', color: '#ec4899' },
    tenant: { label: 'Tenant', labelEs: 'Inquilino', color: '#06b6d4' },
    property_manager: { label: 'Property Manager', labelEs: 'Administrador', color: '#10b981' },
    other: { label: 'Other', labelEs: 'Otro', color: '#6b7280' }
  };

  // Invoice state defaults
  const defaultInvoice = {
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    payment_terms: 'net_30',
    total_amount: 0,
    amount_paid: 0,
    balance_due: 0,
    status: 'draft',
    description: 'Services as per Proposal'
  };

  // Change Order state defaults
  const defaultChangeOrder = {
    change_order_number: '',
    change_order_date: new Date().toISOString().split('T')[0],
    description: '',
    additional_cost: 0,
    status: 'pending'
  };

  // Dynamic company info from settings
  const companyInfo = {
    name: settings.name || COMPANY.name,
    license: settings.license || COMPANY.license,
    license2: settings.license2 || '',
    license3: settings.license3 || '',
    license4: settings.license4 || '',
    license5: settings.license5 || '',
    tagline: settings.tagline || COMPANY.tagline,
    address: settings.address || COMPANY.address,
    city: settings.city || COMPANY.city,
    state: settings.state || COMPANY.state,
    zip: settings.zip || COMPANY.zip,
    phone: settings.phone || COMPANY.phone,
    email: settings.email || COMPANY.email,
    website: settings.website || COMPANY.website,
    primaryColor: settings.primaryColor || COMPANY.primaryColor,
    logo: settings.logo,
    introductionLetter: settings.introductionLetter || '',
    reviews: settings.reviews || ''
  };

  // Helper to get all non-empty licenses as a string
  const getAllLicenses = () => {
    const licenses = [
      companyInfo.license,
      companyInfo.license2,
      companyInfo.license3,
      companyInfo.license4,
      companyInfo.license5
    ].filter(l => l && l.trim() !== '');
    return licenses.join(' • ');
  };

  // Lead Status Options
  const leadStatusOptions = [
    { value: 'estimate_provided', label: language === 'es' ? 'Presupuesto Enviado' : 'Estimate Provided', color: 'bg-blue-100 text-blue-700' },
    { value: 'follow_up', label: language === 'es' ? 'Seguimiento' : 'Follow Up', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'presentation_made', label: language === 'es' ? 'Presentación Hecha' : 'Presentation Made', color: 'bg-purple-100 text-purple-700' },
    { value: 'lead_won', label: language === 'es' ? 'Lead Ganado' : 'Lead Won', color: 'bg-green-100 text-green-700' },
    { value: 'lead_lost', label: language === 'es' ? 'Lead Perdido' : 'Lead Lost', color: 'bg-red-100 text-red-700' },
    { value: 'cold_lead', label: language === 'es' ? 'Lead Frío' : 'Cold Lead', color: 'bg-gray-100 text-gray-700' },
    { value: 'do_not_contact', label: language === 'es' ? 'No Contactar' : 'Do Not Contact', color: 'bg-red-200 text-red-800' }
  ];

  // Roof Types
  const roofTypes = [
    { value: 'tile', label: language === 'es' ? 'Teja' : 'Tile' },
    { value: 'shingle', label: language === 'es' ? 'Teja Asfáltica' : 'Shingle' },
    { value: 'metal', label: language === 'es' ? 'Metal' : 'Metal' },
    { value: 'flat', label: language === 'es' ? 'Plano' : 'Flat' },
    { value: 'epdm', label: 'EPDM (Commercial)' },
    { value: 'tpo', label: 'TPO (Commercial)' }
  ];

  // Stories
  const storyOptions = [
    { value: '1', label: '1 ' + (language === 'es' ? 'Piso' : 'Story') },
    { value: '2', label: '2 ' + (language === 'es' ? 'Pisos' : 'Stories') },
    { value: '3+', label: '3+ ' + (language === 'es' ? 'Pisos' : 'Stories') }
  ];

  // Roof Pitch Options
  const pitchOptions = [
    { value: 'flat', label: language === 'es' ? 'Plano (0-2:12)' : 'Flat (0-2:12)' },
    { value: 'low', label: language === 'es' ? 'Bajo (3-4:12)' : 'Low (3-4:12)' },
    { value: 'medium', label: language === 'es' ? 'Medio (5-7:12)' : 'Medium (5-7:12)' },
    { value: 'steep', label: language === 'es' ? 'Empinado (8-10:12)' : 'Steep (8-10:12)' },
    { value: 'very_steep', label: language === 'es' ? 'Muy Empinado (11+:12)' : 'Very Steep (11+:12)' }
  ];

  // Unit Types
  const unitTypes = [
    { value: 'SQ', label: 'SQ (Square = 100 SF)' },
    { value: 'SF', label: 'SF (Square Foot)' },
    { value: 'LF', label: 'LF (Linear Foot)' },
    { value: 'EA', label: 'EA (Each)' },
    { value: 'JOB', label: 'JOB (Per Job)' }
  ];

  // Region Options for pricing
  const regionOptions = [
    { value: 'fort_lauderdale', label: 'Fort Lauderdale' },
    { value: 'naples', label: 'Naples' }
  ];

  // Project type labels - memoized to avoid dependency issues
  const projectTypeLabels = useMemo(() => ({
    // Main categories
    kitchen: language === 'es' ? 'Remodelación de Cocina' : 'Kitchen Remodeling',
    bathroom: language === 'es' ? 'Remodelación de Baño' : 'Bathroom Remodeling',
    roofing: language === 'es' ? 'Techos' : 'Roofing',
    pool_construction: language === 'es' ? 'Construcción de Piscina' : 'Pool Construction',
    flooring: language === 'es' ? 'Pisos (Laminado)' : 'Flooring (Laminate)',
    room_painting: language === 'es' ? 'Pintura de Habitación' : 'Room Painting',
    interior_painting: language === 'es' ? 'Pintura Interior' : 'Interior Painting',
    drain_line_replacement: language === 'es' ? 'Reemplazo de Drenaje' : 'Drain Line Replacement',
    water_heater_replacement: language === 'es' ? 'Reemplazo de Calentador' : 'Water Heater Replacement',
    water_mitigation: language === 'es' ? 'Mitigación de Agua' : 'Water Mitigation',
    mold_remediation: language === 'es' ? 'Remediación de Moho' : 'Mold Remediation',
    hvac_replacement: language === 'es' ? 'Reemplazo de HVAC' : 'HVAC Replacement',
    hvac: language === 'es' ? 'HVAC' : 'HVAC',
    window_replacement: language === 'es' ? 'Reemplazo de Ventanas' : 'Window Replacement',
    electrical_panel_upgrade: language === 'es' ? 'Actualización de Panel Eléctrico' : 'Electrical Panel Upgrade',
    electrical: language === 'es' ? 'Eléctrico' : 'Electrical',
    drywall_repair: language === 'es' ? 'Reparación de Drywall' : 'Drywall Repair',
    fence_installation: language === 'es' ? 'Instalación de Cerca' : 'Fence Installation',
    concrete_work: language === 'es' ? 'Trabajo de Concreto' : 'Concrete (Driveway/Patio)',
    stucco_repair: language === 'es' ? 'Reparación de Estuco' : 'Stucco Repair',
    soffit_fascia: language === 'es' ? 'Sofito y Fascia' : 'Soffit & Fascia',
    gutter_installation: language === 'es' ? 'Instalación de Canaletas' : 'Gutter Installation',
    tile_installation: language === 'es' ? 'Instalación de Azulejos' : 'Tile Installation',
    pressure_washing: language === 'es' ? 'Lavado a Presión' : 'Pressure Washing',
    popcorn_ceiling_removal: language === 'es' ? 'Remoción de Techo Popcorn' : 'Popcorn Ceiling Removal',
    home_renovation: language === 'es' ? 'Renovación del Hogar' : 'Home Renovation',
    home_addition: language === 'es' ? 'Ampliación del Hogar' : 'Home Addition',
    garage_conversion: language === 'es' ? 'Conversión de Garaje' : 'Garage Conversion',
    new_construction: language === 'es' ? 'Nueva Construcción' : 'New Construction',
    // Specific roof types
    tile_roof: language === 'es' ? 'Techo de Teja' : 'Tile Roof',
    shingle_roof: language === 'es' ? 'Techo de Tejas Asfálticas' : 'Shingle Roof',
    metal_roof: language === 'es' ? 'Techo de Metal' : 'Metal Roof',
    flat_roof: language === 'es' ? 'Techo Plano' : 'Flat Roof',
    // General
    general: language === 'es' ? 'General' : 'General',
    plumbing: language === 'es' ? 'Plomería' : 'Plumbing',
    // Legacy mappings for backwards compatibility
    home_remodel: language === 'es' ? 'Remodelación de Casa' : 'Home Remodel',
    outdoor_living: language === 'es' ? 'Espacio al Aire Libre' : 'Outdoor Living Space',
    electric: language === 'es' ? 'Eléctrico' : 'Electric',
    windows_doors: language === 'es' ? 'Ventanas y Puertas' : 'Windows & Doors',
    kitchen_remodel: language === 'es' ? 'Cocina' : 'Kitchen',
    bathroom_remodel: language === 'es' ? 'Baño' : 'Bathroom',
    roof_replacement: language === 'es' ? 'Techos' : 'Roofing',
    roof_replacement_tile: language === 'es' ? 'Techos' : 'Roofing',
    metal_roof_replacement: language === 'es' ? 'Techos' : 'Roofing',
    general_renovation: language === 'es' ? 'Remodelación de Casa' : 'Home Remodel',
    house_remodeling: language === 'es' ? 'Remodelación de Casa' : 'House Remodeling',
    // Commercial Projects
    commercial_driveways: language === 'es' ? '🏢 Comercial - Entradas' : '🏢 Commercial - Driveways',
    commercial_drywall: language === 'es' ? '🏢 Comercial - Drywall' : '🏢 Commercial - Drywall',
    commercial_electrical: language === 'es' ? '🏢 Comercial - Eléctrico' : '🏢 Commercial - Electrical',
    commercial_exterior: language === 'es' ? '🏢 Comercial - Exterior' : '🏢 Commercial - Exterior',
    commercial_fence: language === 'es' ? '🏢 Comercial - Cercas' : '🏢 Commercial - Fence',
    commercial_hvac: language === 'es' ? '🏢 Comercial - HVAC' : '🏢 Commercial - HVAC',
    commercial_painting: language === 'es' ? '🏢 Comercial - Pintura' : '🏢 Commercial - Painting',
    commercial_plumbing: language === 'es' ? '🏢 Comercial - Plomería' : '🏢 Commercial - Plumbing',
    commercial_pool: language === 'es' ? '🏢 Comercial - Piscina' : '🏢 Commercial - Pool',
    commercial_roadways: language === 'es' ? '🏢 Comercial - Carreteras' : '🏢 Commercial - Roadways',
    commercial_roofing: language === 'es' ? '🏢 Comercial - Techos' : '🏢 Commercial - Roofing'
  }), [language]);

  useEffect(() => {
    fetchLead();
    fetchAllLeads();
    fetchProjectTypes();
    fetchLineItemTemplates();
    fetchLumpSumTemplates();
    fetchContacts();
  // eslint-disable-next-line
  }, [leadId]);

  // Sync aiMeasurements with lead data when lead is loaded
  useEffect(() => {
    if (lead) {
      setAiMeasurements(prev => ({
        ...prev,
        // Roofing
        roof_sqft: lead.roof_sqft || prev.roof_sqft || '',
        roof_stories: lead.stories || lead.roof_stories || prev.roof_stories || '1',
        roof_pitch: lead.roof_pitch || prev.roof_pitch || 'standard',
        roof_squares: lead.roof_squares || prev.roof_squares || '',
        roof_circumference: lead.roof_circumference || prev.roof_circumference || '',
        roof_ridge: lead.roof_ridge || prev.roof_ridge || '',
        roof_valleys: lead.roof_valleys || prev.roof_valleys || '',
        roof_eaves: lead.roof_eaves || prev.roof_eaves || '',
        roof_rakes: lead.roof_rakes || prev.roof_rakes || '',
        // Kitchen/Bathroom
        kitchen_sqft: lead.kitchen_sqft || prev.kitchen_sqft || '',
        bathroom_sqft: lead.bathroom_sqft || prev.bathroom_sqft || '',
        countertop_lf: lead.countertop_lf || prev.countertop_lf || '',
        cabinet_lf: lead.cabinet_lf || prev.cabinet_lf || '',
        tiling_lf: lead.tiling_lf || prev.tiling_lf || '',
        layout_complexity: lead.layout_complexity || prev.layout_complexity || 'moderate',
        finish_level: lead.finish_level || prev.finish_level || 'standard',
        num_bathrooms: lead.num_bathrooms || prev.num_bathrooms || '1',
        // Flooring
        flooring_sqft: lead.flooring_sqft || prev.flooring_sqft || '',
        baseboard_lf: lead.baseboard_lf || prev.baseboard_lf || '',
        subfloor_condition: lead.subfloor_condition || prev.subfloor_condition || 'good',
        flooring_type: lead.flooring_type || prev.flooring_type || 'lvp',
        // Painting
        paint_sqft: lead.paint_sqft || prev.paint_sqft || '',
        paint_coats: lead.paint_coats || prev.paint_coats || '2',
        trim_lf: lead.trim_lf || prev.trim_lf || '',
        wall_height: lead.wall_height || prev.wall_height || 'standard',
        surface_condition: lead.surface_condition || prev.surface_condition || 'good',
        // Pool
        pool_volume: lead.pool_volume || prev.pool_volume || '',
        pool_surface_sqft: lead.pool_surface_sqft || prev.pool_surface_sqft || '',
        deck_sqft: lead.deck_sqft || prev.deck_sqft || '',
        pool_perimeter_lf: lead.pool_perimeter_lf || prev.pool_perimeter_lf || '',
        pool_equipment: lead.pool_equipment || prev.pool_equipment || 'standard',
        soil_type: lead.soil_type || prev.soil_type || 'normal',
        pool_type: lead.pool_type || prev.pool_type || 'gunite',
        // Drain Line
        drain_pipe_lf: lead.drain_pipe_lf || prev.drain_pipe_lf || '',
        pipe_diameter: lead.pipe_diameter || prev.pipe_diameter || '4',
        pipe_depth: lead.pipe_depth || prev.pipe_depth || '',
        access_points: lead.access_points || prev.access_points || '1',
        pipe_material: lead.pipe_material || prev.pipe_material || 'pvc',
        // HVAC
        hvac_tonnage: lead.hvac_tonnage || prev.hvac_tonnage || '',
        indoor_units: lead.indoor_units || prev.indoor_units || '1',
        outdoor_units: lead.outdoor_units || prev.outdoor_units || '1',
        refrigerant_line_lf: lead.refrigerant_line_lf || prev.refrigerant_line_lf || '',
        ductwork_modification: lead.ductwork_modification || prev.ductwork_modification || 'no',
        // Mold
        mold_sqft: lead.mold_sqft || prev.mold_sqft || '',
        containment_sqft: lead.containment_sqft || prev.containment_sqft || '',
        affected_material: lead.affected_material || prev.affected_material || 'drywall',
        water_category: lead.water_category || prev.water_category || '1',
        air_testing: lead.air_testing || prev.air_testing || 'no',
        // Drywall
        drywall_sqft: lead.drywall_sqft || prev.drywall_sqft || '',
        seams_lf: lead.seams_lf || prev.seams_lf || '',
        finish_level_drywall: lead.finish_level_drywall || prev.finish_level_drywall || '4',
        // Fence
        fence_lf: lead.fence_lf || prev.fence_lf || '',
        fence_height: lead.fence_height || prev.fence_height || '6',
        fence_posts: lead.fence_posts || prev.fence_posts || '',
        fence_gates: lead.fence_gates || prev.fence_gates || '1',
        fence_material: lead.fence_material || prev.fence_material || 'wood',
        // General
        total_sqft: lead.total_sqft || prev.total_sqft || '',
        num_rooms: lead.num_rooms || prev.num_rooms || '',
      }));
      
      // Sync AI Project Category from lead data
      if (lead.project_category && !aiProjectCategory) {
        setAiProjectCategory(lead.project_category);
      }
    }
  }, [lead]);
  
  // Note: We don't re-filter the library when project category changes.
  // The library now loads ALL items and users can search across all categories.
  // This allows users to search for items from any project type (e.g., "hvac", "gutter")
  // regardless of the lead's current project category.

  const fetchLead = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/estimates/${leadId}`);
      if (response.ok) {
        const data = await response.json();
        // Ensure arrays and roof details exist
        data.general_fees = data.general_fees || [];
        data.line_items = data.line_items || [];
        data.misc_items = data.misc_items || []; // New miscellaneous items
        data.notes = data.notes || [];
        data.documents = data.documents || [];
        data.photos = data.photos || [];
        data.estimate_images = data.estimate_images || []; // Images for the estimate document
        
        // Initialize estimate_variants if not present
        // Each variant has: { id, project_type, estimate_type, line_items, lump_sum_items, misc_items, lump_sum_total }
        if (!data.estimate_variants || data.estimate_variants.length === 0) {
          // Migrate existing estimate data to first variant
          data.estimate_variants = [{
            id: crypto.randomUUID(),
            project_type: data.project_category || data.project_type || 'General Estimate',
            estimate_type: data.estimate_type || 'itemized',
            line_items: data.line_items || [],
            lump_sum_items: data.lump_sum_items || [],
            misc_items: data.misc_items || [],
            lump_sum_total: data.lump_sum_total || 0,
            tax_rate: data.tax_rate || 7,
            apply_tax: data.apply_tax !== false
          }];
        }
        // Roof details defaults
        data.roof_type = data.roof_type || 'tile';
        data.roof_stories = data.roof_stories || '1';
        data.roof_pitch = data.roof_pitch || 'medium';
        data.roof_size_squares = data.roof_size_squares || 0;
        data.roof_notes = data.roof_notes || '';
        data.pricing_region = data.pricing_region || 'fort_lauderdale';
        // Default itemized pricing to true (show all prices)
        if (data.show_itemized_pricing === undefined) {
          data.show_itemized_pricing = true;
        }
        // Default estimate type to itemized
        if (!data.estimate_type) {
          data.estimate_type = 'itemized';
        }
        // Sync AI Generator type with lead's estimate type
        setAiEstimateType(data.estimate_type || 'itemized');
        // Default lump sum items array
        if (!data.lump_sum_items) {
          data.lump_sum_items = [];
        }
        // Default lump sum total
        if (data.lump_sum_total === undefined) {
          data.lump_sum_total = 0;
        }
        // Do NOT default terms_and_conditions - leave empty to use settings defaults
        // Individual leads can override with custom terms if needed
        if (data.terms_and_conditions === undefined) {
          data.terms_and_conditions = '';
        }
        // Introduction letter - leave blank for new leads to use settings default
        if (data.introduction_letter === undefined) {
          data.introduction_letter = '';
        }
        // Reviews content - leave blank for new leads to customize
        if (data.reviews_content === undefined) {
          data.reviews_content = '';
        }
        // Show intro letter in PDF by default
        if (data.show_intro_letter === undefined) {
          data.show_intro_letter = true;
        }
        if (data.show_reviews === undefined) {
          data.show_reviews = true;
        }
        if (data.show_photos_in_estimate === undefined) {
          data.show_photos_in_estimate = true;
        }
        // Initialize invoice data
        if (!data.invoice) {
          const estimateTotal = (data.line_items || []).reduce((sum, item) => {
            return sum + ((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0));
          }, 0);
          const overheadAmt = data.apply_overhead ? estimateTotal * 0.10 : 0;
          const profitAmt = data.apply_profit ? (estimateTotal + overheadAmt) * 0.10 : 0;
          const grandTotal = estimateTotal + overheadAmt + profitAmt;
          data.invoice = {
            invoice_number: data.proposal_id || data.estimate_number || `INV-${Date.now()}`,
            invoice_date: new Date().toISOString().split('T')[0],
            due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            payment_terms: 'net_30',
            total_amount: grandTotal,
            amount_paid: 0,
            balance_due: grandTotal,
            status: 'draft',
            description: 'Services as per Proposal'
          };
        }
        // Initialize change orders array
        if (!data.change_orders) {
          data.change_orders = [];
        }
        setLead(data);
      } else {
        toast.error('Lead not found');
        navigate('/leads');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllLeads = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/estimates`);
      if (response.ok) {
        const data = await response.json();
        setAllLeads(data);
      }
    } catch (error) {
      console.error('Error fetching all leads:', error);
    }
  };

  // Navigation between leads
  const currentLeadIndex = allLeads.findIndex(l => l.id === leadId);
  const prevLead = currentLeadIndex > 0 ? allLeads[currentLeadIndex - 1] : null;
  const nextLead = currentLeadIndex < allLeads.length - 1 ? allLeads[currentLeadIndex + 1] : null;

  const navigateToLead = (targetLeadId) => {
    if (targetLeadId) {
      navigate(`/leads/${targetLeadId}`);
    }
  };

  const fetchProjectTypes = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/project-types`);
      if (response.ok) setProjectTypes(await response.json());
    } catch (error) { console.error('Error:', error); }
  };

  const fetchLineItemTemplates = async (projectType = null) => {
    try {
      // Build URL with project_type filter if provided
      let url = `${BACKEND_URL}/api/line-item-templates`;
      if (projectType) {
        // Map roofing + roof_type to specific line item type
        let filterType = projectType;
        if (projectType === 'roofing' && aiRoofType) {
          // Map roof type to line item category
          const roofTypeMapping = {
            'concrete_tile': 'tile_roof',
            'clay_tile': 'tile_roof',
            'tile': 'tile_roof',
            'shingle': 'shingle_roof',
            'architectural_shingles': 'shingle_roof',
            'asphalt_shingle': 'shingle_roof',
            '3_tab': 'shingle_roof',
            'metal': 'metal_roof',
            'standing_seam': 'metal_roof',
            'flat': 'flat_roof',
            'tpo': 'flat_roof',
            'modified_bitumen': 'flat_roof'
          };
          filterType = roofTypeMapping[aiRoofType] || 'tile_roof';
        }
        url += `?project_type=${filterType}`;
      }
      const response = await fetch(url);
      if (response.ok) setLineItemTemplates(await response.json());
    } catch (error) { console.error('Error:', error); }
  };

  const fetchLumpSumTemplates = async (projectType = null) => {
    try {
      // Use the same line item templates for lump sum scope items
      let url = `${BACKEND_URL}/api/line-item-templates`;
      if (projectType) {
        // Map roofing + roof_type to specific line item type
        let filterType = projectType;
        if (projectType === 'roofing' && aiRoofType) {
          const roofTypeMapping = {
            'concrete_tile': 'tile_roof',
            'clay_tile': 'tile_roof',
            'tile': 'tile_roof',
            'shingle': 'shingle_roof',
            'architectural_shingles': 'shingle_roof',
            'asphalt_shingle': 'shingle_roof',
            '3_tab': 'shingle_roof',
            'metal': 'metal_roof',
            'standing_seam': 'metal_roof',
            'flat': 'flat_roof',
            'tpo': 'flat_roof',
            'modified_bitumen': 'flat_roof'
          };
          filterType = roofTypeMapping[aiRoofType] || 'tile_roof';
        }
        url += `?project_type=${filterType}`;
      }
      const response = await fetch(url);
      if (response.ok) setLumpSumTemplates(await response.json());
    } catch (error) { console.error('Error:', error); }
  };

  // Contacts functions
  const fetchContacts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/contacts/lead/${leadId}`);
      if (response.ok) {
        const data = await response.json();
        setContacts(data);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const openContactModal = (contact = null) => {
    if (contact) {
      setEditingContact(contact);
      setContactForm({
        name: contact.name || '',
        role: contact.role || 'homeowner',
        company: contact.company || '',
        email: contact.email || '',
        phone: contact.phone || '',
        mobile: contact.mobile || '',
        notes: contact.notes || ''
      });
    } else {
      setEditingContact(null);
      setContactForm({ name: '', role: 'homeowner', company: '', email: '', phone: '', mobile: '', notes: '' });
    }
    setShowContactModal(true);
  };

  const closeContactModal = () => {
    setShowContactModal(false);
    setEditingContact(null);
    setContactForm({ name: '', role: 'homeowner', company: '', email: '', phone: '', mobile: '', notes: '' });
  };

  const saveContact = async () => {
    if (!contactForm.name.trim()) {
      toast.error(language === 'es' ? 'El nombre es requerido' : 'Name is required');
      return;
    }
    try {
      const endpoint = editingContact
        ? `${BACKEND_URL}/api/contacts/${editingContact.id}`
        : `${BACKEND_URL}/api/contacts`;
      const method = editingContact ? 'PUT' : 'POST';
      const body = { ...contactForm, lead_id: leadId };
      
      const response = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        toast.success(editingContact 
          ? (language === 'es' ? 'Contacto actualizado' : 'Contact updated')
          : (language === 'es' ? 'Contacto agregado' : 'Contact added'));
        closeContactModal();
        fetchContacts();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al guardar contacto' : 'Error saving contact');
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar este contacto?' : 'Delete this contact?')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/contacts/${contactId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success(language === 'es' ? 'Contacto eliminado' : 'Contact deleted');
        fetchContacts();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Lump Sum Item handlers
  const addLumpSumItem = (template = null) => {
    const newItem = {
      id: Date.now().toString(),
      category: template?.category || '',
      description: language === 'es' && template?.description_es ? template.description_es : (template?.description || '')
    };
    setLead(prev => ({
      ...prev,
      lump_sum_items: [...(prev.lump_sum_items || []), newItem]
    }));
    setShowLumpSumLibrary(false);
  };

  const addAllLumpSumFromCategory = (items) => {
    const newItems = items.map(template => ({
      id: Date.now().toString() + Math.random(),
      category: template.category,
      description: template.title || template.description || (language === 'es' && template.description_es ? template.description_es : template.description)
    }));
    setLead(prev => ({
      ...prev,
      lump_sum_items: [...(prev.lump_sum_items || []), ...newItems]
    }));
    setShowLumpSumLibrary(false);
    toast.success(`${newItems.length} items added`);
  };

  const handleLumpSumItemChange = (id, field, value) => {
    setLead(prev => ({
      ...prev,
      lump_sum_items: (prev.lump_sum_items || []).map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeLumpSumItem = (id) => {
    setLead(prev => ({
      ...prev,
      lump_sum_items: (prev.lump_sum_items || []).filter(item => item.id !== id)
    }));
  };

  // Filtered lump sum templates based on search
  const filteredLumpSumTemplates = useMemo(() => {
    if (!lumpSumSearchQuery) return lumpSumTemplates;
    const query = lumpSumSearchQuery.toLowerCase();
    return lumpSumTemplates.filter(t =>
      t.title?.toLowerCase().includes(query) ||
      t.category?.toLowerCase().includes(query) ||
      t.project_type?.toLowerCase().includes(query) ||
      t.project_name?.toLowerCase().includes(query) ||
      t.description?.toLowerCase().includes(query) ||
      t.description_es?.toLowerCase().includes(query)
    );
  }, [lumpSumTemplates, lumpSumSearchQuery]);

  // AI Estimate Generator Functions
  const handleGenerateAIEstimate = async () => {
    if (!aiPrompt.trim() && !aiProjectCategory) {
      toast.error(language === 'es' ? 'Por favor seleccione categoría o ingrese descripción' : 'Please select category or enter description');
      return;
    }
    
    // Category validation - if lead has a project_category set, AI should respect it
    const leadCategory = lead.project_category || '';
    const selectedCategory = aiProjectCategory || '';
    
    // Check if user is trying to generate an estimate for a different category than the lead
    if (leadCategory && selectedCategory && leadCategory !== selectedCategory) {
      toast.error(
        language === 'es' 
          ? `La categoría del proyecto está configurada como "${leadCategory}". Por favor cambie la categoría del lead primero si desea un presupuesto de "${selectedCategory}".`
          : `The project category is set to "${leadCategory}". Please change the lead category first if you want a "${selectedCategory}" estimate.`
      );
      return;
    }
    
    setAiGenerating(true);
    setAiCalculatedCost(null);
    setAiItemizedEstimate([]);
    
    try {
      // Use the lead's project category if set, otherwise use the selected category
      const categoryToUse = leadCategory || selectedCategory;
      
      // For roofing projects, auto-fill measurements from lead if available
      let measurementsToUse = { ...aiMeasurements };
      if (categoryToUse === 'roofing' && lead) {
        measurementsToUse = {
          ...measurementsToUse,
          roofArea: lead.roof_area || measurementsToUse.roofArea || '',
          roofPitch: lead.roof_pitch || measurementsToUse.roofPitch || '',
          roofType: lead.roof_type || aiRoofType || ''
        };
      }
      
      const response = await fetch(`${BACKEND_URL}/api/ai/generate-estimate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: aiPrompt,
          project_category: categoryToUse,
          lead_category: leadCategory, // Send lead's category for validation
          quality_tier: aiQualityTier,
          roof_type: aiRoofType || lead.roof_type,
          region: aiRegion,
          measurements: measurementsToUse,
          include_pricing: true,
          estimate_type: aiEstimateType,
          language: language
        })
      });
      
      if (!response.ok) throw new Error('Failed to generate estimate');
      
      const data = await response.json();
      if (data.success && data.estimate) {
        setAiGeneratedEstimate(data.estimate);
        setAiPricingData(data.pricing_data || []);
        
        // For itemized estimates, use the new itemized_scope_items from backend
        if (aiEstimateType === 'itemized' && data.itemized_scope_items && data.itemized_scope_items.length > 0) {
          // Use the simplified scope items with default prices
          const editableItems = data.itemized_scope_items.map((item, idx) => ({
            id: item.id || `ai_${Date.now()}_${idx}`,
            title: item.title,
            description: item.description || '',
            unit: item.unit || 'EA',
            quantity: item.quantity || 1,
            unit_price: item.unit_price || 0,
            total: item.total || 0,
            editable: true
          }));
          setAiItemizedEstimate(editableItems);
          
          // Calculate total for display
          const total = editableItems.reduce((sum, item) => sum + (item.total || 0), 0);
          setAiCalculatedCost({
            items: editableItems,
            total: total,
            primaryQty: editableItems.length,
            qtyLabel: 'items'
          });
        } else if (data.pricing_data && data.pricing_data.length > 0) {
          // Fallback: Calculate costs based on measurements and pricing (for lump sum)
          const calculatedItems = calculateEstimateCosts(data.pricing_data, aiMeasurements, aiProjectCategory);
          setAiCalculatedCost(calculatedItems);
        }
        
        toast.success(language === 'es' ? '¡Estimado generado!' : 'Estimate generated!');
      } else {
        throw new Error('Invalid response');
      }
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error(language === 'es' ? 'Error al generar estimado' : 'Error generating estimate');
    } finally {
      setAiGenerating(false);
    }
  };

  // Update itemized estimate line item
  const updateAiItemizedLine = (itemId, field, value) => {
    setAiItemizedEstimate(prev => prev.map(item => {
      if (item.id === itemId) {
        const updated = { ...item, [field]: value };
        // Recalculate total if quantity or unit_price changes
        if (field === 'quantity' || field === 'unit_price') {
          updated.total = (parseFloat(updated.quantity) || 0) * (parseFloat(updated.unit_price) || 0);
        }
        return updated;
      }
      return item;
    }));
  };

  // Delete itemized line item
  const deleteAiItemizedLine = (itemId) => {
    setAiItemizedEstimate(prev => prev.filter(item => item.id !== itemId));
  };

  // Get itemized total
  const getAiItemizedTotal = () => {
    return aiItemizedEstimate.reduce((sum, item) => sum + (item.total || 0), 0);
  };

  // Apply itemized estimate to proposal
  const applyAIItemizedToProposal = () => {
    if (aiItemizedEstimate.length === 0) return;
    
    // Convert to line items format
    const lineItems = aiItemizedEstimate.map(item => ({
      id: item.id,
      title: item.title,
      description: item.description,
      unit: item.unit,
      quantity: item.quantity,
      unit_price: item.unit_price,
      price: item.total
    }));
    
    setLead(prev => ({
      ...prev,
      estimate_type: 'itemized',
      line_items: [...(prev.line_items || []), ...lineItems],
      project_description: aiGeneratedEstimate?.project_title || prev.project_description
    }));
    
    toast.success(language === 'es' 
      ? `${lineItems.length} items agregados a la propuesta` 
      : `${lineItems.length} items added to proposal`);
    
    // Clear AI state
    setAiGeneratedEstimate(null);
    setAiItemizedEstimate([]);
    setAiCalculatedCost(null);
    setAiPrompt('');
  };

  // Calculate estimate costs based on measurements and pricing data
  const calculateEstimateCosts = (pricingData, measurements, category) => {
    const calculatedItems = [];
    let totalCost = 0;
    
    // Get relevant measurement based on category
    let primaryQty = 0;
    let qtyLabel = 'units';
    
    if (category === 'roofing') {
      primaryQty = parseFloat(measurements.roof_sqft) || 0;
      qtyLabel = 'sq ft';
    } else if (category === 'kitchen') {
      primaryQty = parseFloat(measurements.kitchen_sqft) || 0;
      qtyLabel = 'sq ft';
    } else if (category === 'bathroom') {
      primaryQty = parseFloat(measurements.bathroom_sqft) || 0;
      qtyLabel = 'sq ft';
    } else if (category === 'pool') {
      const l = parseFloat(measurements.pool_length) || 0;
      const w = parseFloat(measurements.pool_width) || 0;
      primaryQty = l * w;
      qtyLabel = 'sq ft';
    } else {
      primaryQty = parseFloat(measurements.total_sqft) || 0;
      qtyLabel = 'sq ft';
    }
    
    // Group pricing by subcategory
    const bySubcategory = {};
    pricingData.forEach(item => {
      const sub = item.subcategory || 'general';
      if (!bySubcategory[sub]) bySubcategory[sub] = [];
      bySubcategory[sub].push(item);
    });
    
    // Calculate costs for each subcategory
    Object.entries(bySubcategory).forEach(([subcategory, items]) => {
      items.forEach(item => {
        let qty = 0;
        let itemTotal = 0;
        const price = item.regional_price || 0;
        
        // Determine quantity based on unit type
        if (item.unit === 'sq_ft') {
          qty = primaryQty;
        } else if (item.unit === 'linear_ft') {
          // For countertops/cabinets, use specific measurements
          if (subcategory === 'countertops') {
            qty = parseFloat(measurements.countertop_linear_ft) || (primaryQty * 0.15); // estimate 15% of sqft as linear ft
          } else if (subcategory === 'cabinets') {
            qty = parseFloat(measurements.cabinet_linear_ft) || (primaryQty * 0.3); // estimate 30% of sqft as linear ft
          } else {
            qty = primaryQty * 0.1; // default estimate
          }
        } else if (item.unit === 'each') {
          qty = 1; // Default to 1 for each items
          if (subcategory === 'appliances') qty = 1;
          if (subcategory === 'toilet') qty = parseFloat(measurements.num_bathrooms) || 1;
          if (subcategory === 'permits') qty = 1;
        } else if (item.unit === 'cubic_ft') {
          const depth = parseFloat(measurements.pool_depth) || 5;
          qty = primaryQty * depth;
        } else {
          qty = 1;
        }
        
        if (qty > 0 && price > 0) {
          itemTotal = qty * price;
          totalCost += itemTotal;
          
          calculatedItems.push({
            name: item.item_name,
            description: item.description,
            subcategory: subcategory,
            unit: item.unit,
            quantity: qty,
            unit_price: price,
            total: itemTotal
          });
        }
      });
    });
    
    return {
      items: calculatedItems,
      total: totalCost,
      primaryQty: primaryQty,
      qtyLabel: qtyLabel
    };
  };

  const applyAIEstimateToProposal = () => {
    if (!aiGeneratedEstimate?.scope_items && !aiItemizedEstimate?.length) return;
    
    // Check which estimate type is selected
    if (aiEstimateType === 'itemized') {
      let newLineItems = [];
      
      // Use aiItemizedEstimate if available (has quantities and prices from backend)
      if (aiItemizedEstimate && aiItemizedEstimate.length > 0) {
        newLineItems = aiItemizedEstimate.map((item, idx) => ({
          id: `ai_${Date.now()}_${idx}`,
          title: item.title,
          description: item.description || '',
          category: item.category || 'Scope of Work',
          unit: item.unit || 'EA',
          quantity: item.quantity || 1,
          unit_price: item.unit_price || 0,
          total: item.total || ((item.quantity || 1) * (item.unit_price || 0))
        }));
      } else {
        // Fallback: convert AI items to editable line items
        let itemNumber = 1;
        aiGeneratedEstimate.scope_items.forEach(section => {
          section.items?.forEach(item => {
            newLineItems.push({
              id: `ai_${Date.now()}_${itemNumber}`,
              title: `${itemNumber}. ${item.title}`,
              description: item.description || '',
              category: section.category,
              unit: item.unit || 'EA',
              quantity: item.quantity || 1,
              unit_price: 0,
              total: 0
            });
            itemNumber++;
          });
        });
      }
      
      // Calculate total for toast message
      const totalValue = newLineItems.reduce((sum, item) => sum + (item.total || 0), 0);
      const hasPrice = totalValue > 0;
      
      // REPLACE line_items entirely (not append) for itemized estimate
      setLead(prev => ({
        ...prev,
        estimate_type: 'itemized',
        line_items: newLineItems,  // Replace, not append
        lump_sum_items: [],  // Clear lump sum items when switching to itemized
        project_description: aiGeneratedEstimate?.project_title || prev.project_description
      }));
      
      if (hasPrice) {
        toast.success(language === 'es' 
          ? `Propuesta reemplazada con ${newLineItems.length} items - Total: $${totalValue.toLocaleString()}` 
          : `Proposal replaced with ${newLineItems.length} items - Total: $${totalValue.toLocaleString()}`);
      } else {
        toast.success(language === 'es' 
          ? `Propuesta reemplazada con ${newLineItems.length} items - agregue precios abajo` 
          : `Proposal replaced with ${newLineItems.length} items - add prices below`);
      }
    } else {
      // For lump sum: convert AI items to scope descriptions
      const newLumpSumItems = [];
      aiGeneratedEstimate.scope_items.forEach(section => {
        section.items?.forEach(item => {
          newLumpSumItems.push({
            id: Date.now().toString() + Math.random(),
            category: section.category,
            description: `${item.title}: ${item.description}`
          });
        });
      });
      
      // REPLACE lump sum items entirely (not append)
      setLead(prev => ({
        ...prev,
        estimate_type: 'lump_sum',
        lump_sum_items: newLumpSumItems,  // Replace, not append
        line_items: [],  // Clear line items when switching to lump sum
        project_description: aiGeneratedEstimate.project_title || prev.project_description
      }));
      
      toast.success(language === 'es' 
        ? `Propuesta reemplazada con ${newLumpSumItems.length} items` 
        : `Proposal replaced with ${newLumpSumItems.length} items`);
    }
    
    // Clear AI state
    setAiGeneratedEstimate(null);
    setAiItemizedEstimate([]);
    setAiPrompt('');
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        
        // Convert to base64 and send for transcription
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64Audio = reader.result.split(',')[1];
          
          try {
            toast.loading(language === 'es' ? 'Transcribiendo...' : 'Transcribing...');
            const response = await fetch(`${BACKEND_URL}/api/ai/transcribe-voice`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ audio_data: base64Audio })
            });
            
            toast.dismiss();
            
            if (response.ok) {
              const data = await response.json();
              if (data.success && data.text) {
                setAiPrompt(prev => prev ? `${prev} ${data.text}` : data.text);
                toast.success(language === 'es' ? 'Voz transcrita' : 'Voice transcribed');
              }
            } else {
              toast.error(language === 'es' ? 'Error al transcribir' : 'Error transcribing');
            }
          } catch (error) {
            toast.dismiss();
            console.error('Transcription error:', error);
            toast.error(language === 'es' ? 'Error al transcribir' : 'Error transcribing');
          }
        };
        reader.readAsDataURL(audioBlob);
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.info(language === 'es' ? 'Grabando...' : 'Recording...');
    } catch (error) {
      console.error('Recording error:', error);
      toast.error(language === 'es' ? 'No se pudo acceder al micrófono' : 'Could not access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Photo-to-Estimate Analysis - Step 1: Show modal for reference measurement
  const handlePhotoSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error(language === 'es' ? 'Solo se permiten imágenes JPEG, PNG o WEBP' : 'Only JPEG, PNG, or WEBP images allowed');
      return;
    }
    
    // Store the file and show the reference measurement modal
    setPendingPhotoFile(file);
    setShowPhotoMeasureModal(true);
  };

  // Photo-to-Estimate Analysis - Step 2: Process with reference measurement
  const handlePhotoAnalysis = async () => {
    if (!pendingPhotoFile) return;
    
    setShowPhotoMeasureModal(false);
    
    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64Image = reader.result.split(',')[1];
      
      setPhotoAnalyzing(true);
      toast.loading(language === 'es' ? 'Midiendo y analizando...' : 'Measuring and analyzing...');
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/ai/analyze-photo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            image_base64: base64Image,
            project_hint: aiProjectCategory || '',
            reference_object: photoReferenceObject,
            reference_measurement: parseFloat(photoReferenceMeasurement) || 0,
            reference_unit: 'ft'
          })
        });
        
        toast.dismiss();
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.analysis) {
            setPhotoAnalysis(data.analysis);
            toast.success(language === 'es' ? '¡Mediciones completadas!' : 'Measurements complete!');
            
            // Auto-fill measurements from the new format
            const measurements = data.analysis.measurements || {};
            if (measurements.total_sqft) {
              setAiMeasurements(prev => ({
                ...prev,
                roof_sqft: measurements.total_sqft.toString(),
                total_sqft: measurements.total_sqft.toString()
              }));
            }
            if (measurements.length_ft && measurements.width_ft) {
              // Store additional measurements
              setPhotoAnalysis(prev => ({
                ...prev,
                ...data.analysis,
                length_ft: measurements.length_ft,
                width_ft: measurements.width_ft,
                perimeter_ft: measurements.perimeter_ft
              }));
            }
            // Set project category based on analysis
            if (data.analysis.project_type) {
              const typeMap = {
                'roof': 'roofing',
                'roof_replacement': 'roofing',
                'kitchen': 'kitchen',
                'kitchen_remodel': 'kitchen',
                'bathroom': 'bathroom',
                'bathroom_remodel': 'bathroom',
                'pool': 'pool',
                'driveway': 'concrete',
                'patio': 'concrete',
                'fence': 'fence'
              };
              const category = typeMap[data.analysis.project_type] || data.analysis.project_type;
              setAiProjectCategory(category);
            }
          }
        } else {
          toast.error(language === 'es' ? 'Error al medir' : 'Error measuring');
        }
      } catch (error) {
        toast.dismiss();
        console.error('Photo analysis error:', error);
        toast.error(language === 'es' ? 'Error al analizar foto' : 'Error analyzing photo');
      } finally {
        setPhotoAnalyzing(false);
        setPendingPhotoFile(null);
        setPhotoReferenceObject('');
        setPhotoReferenceMeasurement('');
      }
    };
    reader.readAsDataURL(pendingPhotoFile);
  };

  // AI Email Writer
  const handleGenerateEmail = async () => {
    setEmailGenerating(true);
    toast.loading(language === 'es' ? 'Generando email...' : 'Generating email...');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai/generate-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email_type: emailType,
          client_name: lead.client_name || 'Valued Customer',
          client_email: lead.email || '',
          project_type: lead.project_type || aiProjectCategory || 'construction',
          estimate_total: lead.grand_total || 0,
          custom_context: customEmailContext,
          language: language
        })
      });
      
      toast.dismiss();
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.email) {
          setGeneratedEmail(data.email);
          toast.success(language === 'es' ? '¡Email generado!' : 'Email generated!');
        }
      } else {
        toast.error(language === 'es' ? 'Error al generar email' : 'Error generating email');
      }
    } catch (error) {
      toast.dismiss();
      console.error('Email generation error:', error);
      toast.error(language === 'es' ? 'Error al generar email' : 'Error generating email');
    } finally {
      setEmailGenerating(false);
    }
  };

  // Send generated email
  const handleSendGeneratedEmail = async () => {
    if (!generatedEmail || !lead.email) {
      toast.error(language === 'es' ? 'Email del cliente requerido' : 'Client email required');
      return;
    }
    
    try {
      toast.loading(language === 'es' ? 'Enviando email...' : 'Sending email...');
      
      const response = await fetch(`${BACKEND_URL}/api/emails/send-proposal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to_email: lead.email,
          to_name: lead.client_name,
          subject: generatedEmail.subject,
          html_content: `<div style="font-family: Arial, sans-serif; line-height: 1.6;">${generatedEmail.body.replace(/\n/g, '<br>')}</div>`,
          from_name: companySettings.name || 'JY Mega FC Construction'
        })
      });
      
      toast.dismiss();
      
      if (response.ok) {
        toast.success(language === 'es' ? '¡Email enviado!' : 'Email sent!');
        setShowEmailWriter(false);
        setGeneratedEmail(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || (language === 'es' ? 'Error al enviar' : 'Error sending'));
      }
    } catch (error) {
      toast.dismiss();
      toast.error(language === 'es' ? 'Error al enviar email' : 'Error sending email');
    }
  };

  const handleInputChange = (field, value) => {
    setLead(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!lead.client_name) { 
      toast.error(language === 'es' ? 'Nombre del cliente requerido' : 'Client name is required'); 
      return; 
    }
    setSaving(true);
    try {
      // Calculate totals based on estimate type
      let grandTotal = 0;
      let subtotal = 0;
      let overheadAmount = 0;
      let profitAmount = 0;
      
      if (lead.estimate_type === 'lump_sum') {
        // For lump sum: use the manual lump_sum_total + misc items
        const lumpSumBase = parseFloat(lead.lump_sum_total) || 0;
        const miscTotal = (lead.misc_items || []).reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);
        subtotal = lumpSumBase + miscTotal;
        
        // Calculate Contractor Overhead (10%) if enabled
        const applyOverhead = lead.apply_overhead === true;
        overheadAmount = applyOverhead ? subtotal * 0.10 : 0;
        
        // Calculate Contractor Profit (10%) if enabled
        const applyProfit = lead.apply_profit === true;
        profitAmount = applyProfit ? (subtotal + overheadAmount) * 0.10 : 0;
        
        grandTotal = subtotal + overheadAmount + profitAmount;
      } else {
        // For itemized: calculate from line items + misc items
        const miscTotal = (lead.misc_items || []).reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);
        const itemsTotal = (lead.line_items || []).reduce((sum, item) => {
          const qty = parseFloat(item.quantity) || 0;
          const unitPrice = parseFloat(item.unit_price) || 0;
          return sum + (qty * unitPrice);
        }, 0);
        subtotal = miscTotal + itemsTotal;
        
        // Calculate Contractor Overhead (10%) if enabled
        const applyOverhead = lead.apply_overhead === true;
        overheadAmount = applyOverhead ? subtotal * 0.10 : 0;
        
        // Calculate Contractor Profit (10%) if enabled
        const applyProfit = lead.apply_profit === true;
        profitAmount = applyProfit ? (subtotal + overheadAmount) * 0.10 : 0;
        
        grandTotal = subtotal + overheadAmount + profitAmount;
      }
      
      const updatedLead = {
        ...lead,
        subtotal,
        overhead_amount: overheadAmount,
        profit_amount: profitAmount,
        grand_total: grandTotal
      };

      const response = await fetch(`${BACKEND_URL}/api/estimates/${leadId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedLead)
      });
      if (response.ok) {
        setLead(updatedLead);
        toast.success(language === 'es' ? '¡Lead guardado!' : 'Lead saved!');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'es' ? 'Error al guardar' : 'Error saving');
    } finally {
      setSaving(false);
    }
  };

  // General Fees functions - DEPRECATED, replaced by misc_items
  const handleFeeChange = (id, field, value) => {
    // Keep for backwards compatibility but redirect to misc items
    handleMiscItemChange(id, field, value);
  };

  const addFee = () => {
    addMiscItem();
  };

  const removeFee = (id) => {
    removeMiscItem(id);
  };

  // Miscellaneous Items functions (replaces General Fees)
  const handleMiscItemChange = (id, field, value) => {
    setLead(prev => ({
      ...prev,
      misc_items: (prev.misc_items || []).map(item => 
        item.id === id ? { ...item, [field]: field === 'price' ? parseFloat(value) || 0 : value } : item
      )
    }));
  };

  const addMiscItem = () => {
    setLead(prev => ({
      ...prev,
      misc_items: [...(prev.misc_items || []), { id: crypto.randomUUID(), title: '', description: '', price: 0 }]
    }));
  };

  const removeMiscItem = (id) => {
    setLead(prev => ({
      ...prev,
      misc_items: (prev.misc_items || []).filter(item => item.id !== id)
    }));
  };

  // Helper function to map variant project_type to backend filter type
  const getLibraryFilterType = () => {
    const variantType = getCurrentVariant()?.project_type || '';
    const variantTypeLower = variantType.toLowerCase().replace(/\s+/g, '_');
    
    // Direct mappings for specific project types
    const typeMapping = {
      // Roof types - map display names to backend filter types
      'concrete_tile': 'tile_roof',
      'clay_tile': 'tile_roof',
      'tile_roof': 'tile_roof',
      'shingle': 'shingle_roof',
      'shingle_roof': 'shingle_roof',
      'architectural_shingles': 'shingle_roof',
      'asphalt_shingle': 'shingle_roof',
      'metal': 'metal_roof',
      'metal_roof': 'metal_roof',
      'standing_seam': 'metal_roof',
      'standing_seam_metal': 'metal_roof',
      'flat': 'flat_roof',
      'flat_roof': 'flat_roof',
      'tpo': 'flat_roof',
      'modified_bitumen': 'flat_roof',
      // Other project types
      'kitchen_remodel': 'kitchen',
      'kitchen': 'kitchen',
      'bathroom_remodel': 'bathroom',
      'bathroom': 'bathroom',
      'pool_construction': 'pool_construction',
      'pool': 'pool_construction',
      'home_renovation': 'home_renovation',
      'home_addition': 'home_addition',
      'garage_conversion': 'garage_conversion',
      'mold_remediation': 'mold_remediation',
      'interior_painting': 'interior_painting',
      'painting': 'interior_painting',
      'new_construction': 'new_construction'
    };
    
    // Check direct mapping first
    if (typeMapping[variantTypeLower]) {
      return typeMapping[variantTypeLower];
    }
    
    // Fallback: if roofing is selected with a roof type in AI generator, use that
    if (aiProjectCategory === 'roofing' && aiRoofType) {
      const roofTypeMapping = {
        'concrete_tile': 'tile_roof',
        'clay_tile': 'tile_roof',
        'tile': 'tile_roof',
        'shingle': 'shingle_roof',
        'architectural_shingles': 'shingle_roof',
        'asphalt_shingle': 'shingle_roof',
        '3_tab': 'shingle_roof',
        'metal': 'metal_roof',
        'standing_seam': 'metal_roof',
        'flat': 'flat_roof',
        'tpo': 'flat_roof',
        'modified_bitumen': 'flat_roof'
      };
      return roofTypeMapping[aiRoofType] || 'tile_roof';
    }
    
    // Final fallback - return the variant type or general project category
    return variantType || aiProjectCategory || lead?.project_category || 'general';
  };

  // Estimate Variants functions (multiple estimates per lead by project type)
  const getCurrentVariant = () => {
    if (!lead?.estimate_variants?.length) return null;
    return lead.estimate_variants[currentVariantIndex] || lead.estimate_variants[0];
  };

  const createEstimateVariant = (projectType) => {
    const newVariant = {
      id: crypto.randomUUID(),
      project_type: projectType,
      estimate_type: 'itemized',
      line_items: [],
      lump_sum_items: [],
      misc_items: [],
      lump_sum_total: 0,
      tax_rate: 7,
      apply_tax: true
    };
    
    setLead(prev => ({
      ...prev,
      estimate_variants: [...(prev.estimate_variants || []), newVariant]
    }));
    
    // Switch to the new variant
    setCurrentVariantIndex(lead?.estimate_variants?.length || 0);
    setShowCreateVariant(false);
    setNewVariantType('');
    toast.success(language === 'es' ? `Estimado "${projectType}" creado` : `"${projectType}" estimate created`);
  };

  const deleteEstimateVariant = (variantId) => {
    if (!lead?.estimate_variants) {
      return;
    }
    
    const isLastVariant = lead.estimate_variants.length === 1;
    
    if (isLastVariant) {
      // Clear the last variant and reset to empty state, then show create dialog
      setLead(prev => ({
        ...prev,
        estimate_variants: []
      }));
      setCurrentVariantIndex(0);
      setShowCreateVariant(true); // Open the create new variant dialog
      toast.success(language === 'es' ? 'Estimado eliminado. Cree uno nuevo.' : 'Estimate deleted. Create a new one.');
      return;
    }
    
    const newVariants = lead.estimate_variants.filter(v => v.id !== variantId);
    setLead(prev => ({
      ...prev,
      estimate_variants: newVariants
    }));
    
    // Adjust current index if needed
    if (currentVariantIndex >= newVariants.length) {
      setCurrentVariantIndex(Math.max(0, newVariants.length - 1));
    }
    
    toast.success(language === 'es' ? 'Estimado eliminado' : 'Estimate deleted');
  };

  const updateCurrentVariant = (field, value) => {
    setLead(prev => {
      const updatedVariants = [...(prev.estimate_variants || [])];
      if (updatedVariants[currentVariantIndex]) {
        updatedVariants[currentVariantIndex] = {
          ...updatedVariants[currentVariantIndex],
          [field]: value
        };
      }
      return { ...prev, estimate_variants: updatedVariants };
    });
  };

  // Helper to get/set variant-specific data
  const getVariantLineItems = () => getCurrentVariant()?.line_items || lead?.line_items || [];
  const getVariantLumpSumItems = () => getCurrentVariant()?.lump_sum_items || lead?.lump_sum_items || [];
  const getVariantMiscItems = () => getCurrentVariant()?.misc_items || lead?.misc_items || [];
  const getVariantEstimateType = () => getCurrentVariant()?.estimate_type || lead?.estimate_type || 'itemized';
  const getVariantLumpSumTotal = () => getCurrentVariant()?.lump_sum_total || lead?.lump_sum_total || 0;

  // Line Items functions - updated for unit-based pricing
  const handleLineItemChange = (id, field, value) => {
    setLead(prev => ({
      ...prev,
      line_items: prev.line_items.map(item => {
        if (item.id !== id) return item;
        const updated = { ...item, [field]: value };
        // Auto-calculate total when qty or unit_price changes
        if (field === 'quantity' || field === 'unit_price') {
          const qty = parseFloat(field === 'quantity' ? value : item.quantity) || 0;
          const unitPrice = parseFloat(field === 'unit_price' ? value : item.unit_price) || 0;
          // Round to 2 decimal places to avoid floating point issues
          updated.total = Math.round(qty * unitPrice * 100) / 100;
        }
        return updated;
      })
    }));
  };

  const addLineItem = (template = null) => {
    const getTitle = (t) => language === 'es' && t.title_es ? t.title_es : t.title;
    const getDesc = (t) => language === 'es' && t.description_es ? t.description_es : t.description;
    
    // Default quantity based on roof size if available
    const defaultQty = lead?.roof_size_squares || 1;
    
    const newItem = template 
      ? { 
          id: crypto.randomUUID(), 
          title: getTitle(template), 
          description: getDesc(template), 
          unit: template.unit || 'SQ',
          quantity: template.unit === 'SQ' ? defaultQty : 1,
          unit_price: template.unit_price || template.default_price || 0,
          total: (template.unit === 'SQ' ? defaultQty : 1) * (template.unit_price || template.default_price || 0)
        }
      : { 
          id: crypto.randomUUID(), 
          title: '', 
          description: '', 
          unit: 'SQ',
          quantity: defaultQty,
          unit_price: 0,
          total: 0
        };
    setLead(prev => ({ ...prev, line_items: [...prev.line_items, newItem] }));
  };

  const addCategoryCluster = (templates) => {
    const getTitle = (t) => language === 'es' && t.title_es ? t.title_es : t.title;
    const getDesc = (t) => language === 'es' && t.description_es ? t.description_es : t.description;
    const defaultQty = lead?.roof_size_squares || 1;
    
    const newItems = templates.map(t => {
      const qty = t.unit === 'SQ' ? defaultQty : 1;
      const unitPrice = t.unit_price || t.default_price || 0;
      return { 
        id: crypto.randomUUID(), 
        title: getTitle(t), 
        description: getDesc(t), 
        unit: t.unit || 'SQ',
        quantity: qty,
        unit_price: unitPrice,
        total: qty * unitPrice
      };
    });
    setLead(prev => ({ ...prev, line_items: [...prev.line_items, ...newItems] }));
    setShowTemplateSearch(false);
    toast.success(`${templates.length} ${language === 'es' ? 'partidas agregadas' : 'items added'}`);
  };

  const removeLineItem = (id) => {
    setLead(prev => ({ ...prev, line_items: prev.line_items.filter(item => item.id !== id) }));
  };

  // Move line item up in the list
  const moveLineItemUp = (index) => {
    if (index <= 0) return;
    setLead(prev => {
      const items = [...prev.line_items];
      [items[index - 1], items[index]] = [items[index], items[index - 1]];
      return { ...prev, line_items: items };
    });
  };

  // Move line item down in the list
  const moveLineItemDown = (index) => {
    setLead(prev => {
      if (index >= prev.line_items.length - 1) return prev;
      const items = [...prev.line_items];
      [items[index], items[index + 1]] = [items[index + 1], items[index]];
      return { ...prev, line_items: items };
    });
  };

  // Auto-update quantities when roof size changes
  const handleRoofSizeChange = (newSize) => {
    const size = parseFloat(newSize) || 0;
    handleInputChange('roof_size_squares', size);
    
    // Update all SQ-based line items
    setLead(prev => ({
      ...prev,
      roof_size_squares: size,
      line_items: prev.line_items.map(item => {
        if (item.unit === 'SQ') {
          const newTotal = size * (parseFloat(item.unit_price) || 0);
          return { ...item, quantity: size, total: newTotal };
        }
        return item;
      })
    }));
  };

  // Notes functions
  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/estimates/${leadId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newNote })
      });
      if (response.ok) {
        const noteDoc = await response.json();
        setLead(prev => ({ ...prev, notes: [...(prev.notes || []), noteDoc] }));
        setNewNote('');
        toast.success(language === 'es' ? 'Nota agregada' : 'Note added');
      }
    } catch (error) { console.error('Error adding note:', error); }
  };

  const deleteNote = async (noteId) => {
    try {
      await fetch(`${BACKEND_URL}/api/estimates/${leadId}/notes/${noteId}`, { method: 'DELETE' });
      setLead(prev => ({ ...prev, notes: prev.notes.filter(n => n.id !== noteId) }));
      toast.success(language === 'es' ? 'Nota eliminada' : 'Note deleted');
    } catch (error) { console.error('Error deleting note:', error); }
  };

  // Insurance Notes Functions
  const addInsuranceNote = () => {
    if (!newInsuranceNote.trim()) return;
    const newNoteObj = {
      id: Date.now().toString(),
      content: newInsuranceNote,
      created_at: new Date().toISOString()
    };
    setLead(prev => ({ 
      ...prev, 
      insurance_notes_list: [...(prev.insurance_notes_list || []), newNoteObj] 
    }));
    setNewInsuranceNote('');
    toast.success(language === 'es' ? 'Nota de seguro agregada' : 'Insurance note added');
  };

  const deleteInsuranceNote = (noteId) => {
    setLead(prev => ({ 
      ...prev, 
      insurance_notes_list: (prev.insurance_notes_list || []).filter(n => n.id !== noteId) 
    }));
    toast.success(language === 'es' ? 'Nota eliminada' : 'Note deleted');
  };

  const startEditInsuranceNote = (note) => {
    setEditingInsuranceNote(note.id);
    setEditInsuranceNoteText(note.content);
  };

  const saveEditInsuranceNote = (noteId) => {
    if (!editInsuranceNoteText.trim()) return;
    setLead(prev => ({
      ...prev,
      insurance_notes_list: (prev.insurance_notes_list || []).map(n => 
        n.id === noteId ? { ...n, content: editInsuranceNoteText, updated_at: new Date().toISOString() } : n
      )
    }));
    setEditingInsuranceNote(null);
    setEditInsuranceNoteText('');
    toast.success(language === 'es' ? 'Nota actualizada' : 'Note updated');
  };

  const cancelEditInsuranceNote = () => {
    setEditingInsuranceNote(null);
    setEditInsuranceNoteText('');
  };

  // Project Notes Functions
  const addProjectNote = () => {
    if (!newProjectNote.trim()) return;
    const newNoteObj = {
      id: Date.now().toString(),
      content: newProjectNote,
      created_at: new Date().toISOString()
    };
    setLead(prev => ({ 
      ...prev, 
      project_notes_list: [...(prev.project_notes_list || []), newNoteObj] 
    }));
    setNewProjectNote('');
    toast.success(language === 'es' ? 'Nota de proyecto agregada' : 'Project note added');
  };

  const deleteProjectNote = (noteId) => {
    setLead(prev => ({ 
      ...prev, 
      project_notes_list: (prev.project_notes_list || []).filter(n => n.id !== noteId) 
    }));
    toast.success(language === 'es' ? 'Nota eliminada' : 'Note deleted');
  };

  const startEditProjectNote = (note) => {
    setEditingProjectNote(note.id);
    setEditProjectNoteText(note.content);
  };

  const saveEditProjectNote = (noteId) => {
    if (!editProjectNoteText.trim()) return;
    setLead(prev => ({
      ...prev,
      project_notes_list: (prev.project_notes_list || []).map(n => 
        n.id === noteId ? { ...n, content: editProjectNoteText, updated_at: new Date().toISOString() } : n
      )
    }));
    setEditingProjectNote(null);
    setEditProjectNoteText('');
    toast.success(language === 'es' ? 'Nota actualizada' : 'Note updated');
  };

  const cancelEditProjectNote = () => {
    setEditingProjectNote(null);
    setEditProjectNoteText('');
  };

  // Document Upload
  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Only accept PDF files
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error(language === 'es' ? 'Solo se permiten archivos PDF' : 'Only PDF files are allowed');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    toast.loading(language === 'es' ? `Subiendo ${file.name}...` : `Uploading ${file.name}...`, { id: 'doc-upload' });
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/estimates/${leadId}/documents`, {
        method: 'POST',
        body: formData
      });
      if (response.ok) {
        const result = await response.json();
        setLead(prev => ({ ...prev, documents: [...(prev.documents || []), result] }));
        toast.success(language === 'es' ? `${file.name} subido` : `${file.name} uploaded`, { id: 'doc-upload' });
      } else {
        toast.error(language === 'es' ? 'Error al subir' : 'Upload failed', { id: 'doc-upload' });
      }
    } catch (error) { 
      console.error('Error uploading document:', error);
      toast.error(language === 'es' ? 'Error al subir' : 'Upload failed', { id: 'doc-upload' });
    }
    
    // Reset file input
    e.target.value = '';
  };

  // Photo Upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    toast.loading(language === 'es' ? `Subiendo ${file.name}...` : `Uploading ${file.name}...`, { id: 'photo-upload' });
    
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(`${BACKEND_URL}/api/estimates/${leadId}/photos`, {
        method: 'POST',
        body: formData
      });
      if (response.ok) {
        const result = await response.json();
        setLead(prev => ({ ...prev, photos: [...(prev.photos || []), result] }));
        toast.success(language === 'es' ? 'Foto subida' : 'Photo uploaded', { id: 'photo-upload' });
      } else {
        toast.error(language === 'es' ? 'Error al subir foto' : 'Photo upload failed', { id: 'photo-upload' });
      }
    } catch (error) { 
      console.error('Error uploading photo:', error);
      toast.error(language === 'es' ? 'Error al subir foto' : 'Photo upload failed', { id: 'photo-upload' });
    }
    
    // Reset file input
    e.target.value = '';
  };

  const deleteDocument = async (doc) => {
    // Handle both old format (string) and new format (object)
    const docId = typeof doc === 'object' ? doc.id : doc.split('/').pop();
    try {
      await fetch(`${BACKEND_URL}/api/estimates/${leadId}/documents/${docId}`, { method: 'DELETE' });
      setLead(prev => ({ 
        ...prev, 
        documents: prev.documents.filter(d => {
          if (typeof d === 'object') return d.id !== docId;
          return d !== doc;
        })
      }));
      toast.success(language === 'es' ? 'Documento eliminado' : 'Document deleted');
    } catch (error) { console.error('Error deleting document:', error); }
  };

  const deletePhoto = async (photo) => {
    const photoPath = getPhotoPath(photo);
    const photoId = typeof photo === 'object' && photo.id ? photo.id : (photoPath ? photoPath.split('/').pop() : null);
    if (!photoId) {
      toast.error(language === 'es' ? 'Error: Foto inválida' : 'Error: Invalid photo');
      return;
    }
    try {
      await fetch(`${BACKEND_URL}/api/estimates/${leadId}/photos/${photoId}`, { method: 'DELETE' });
      setLead(prev => ({ 
        ...prev, 
        photos: prev.photos.filter(p => {
          if (typeof p === 'object') return p.id !== photoId;
          return p !== photo;
        })
      }));
      toast.success(language === 'es' ? 'Foto eliminada' : 'Photo deleted');
    } catch (error) { console.error('Error deleting photo:', error); }
  };

  // Helper to get document display name
  const getDocumentName = (doc) => {
    if (typeof doc === 'object') return doc.original_filename;
    // Old format: extract from path
    const filename = doc.split('/').pop();
    // Remove UUID prefix if present
    const parts = filename.split('_');
    return parts.length > 1 ? parts.slice(1).join('_') : filename;
  };

  // Helper to get document URL (supports both GridFS and legacy filesystem)
  const getDocumentUrl = (doc) => {
    if (typeof doc === 'object') {
      // GridFS storage
      if (doc.gridfs_id) return `${BACKEND_URL}/api/files/download/${doc.gridfs_id}`;
      // Legacy filesystem
      if (doc.path) return `${BACKEND_URL}/uploads/${doc.path}`;
    }
    return `${BACKEND_URL}/uploads/${doc}`;
  };
  
  // Helper to get photo URL (supports both GridFS and legacy filesystem)
  const getPhotoUrl = (photo) => {
    if (typeof photo === 'object') {
      // GridFS storage
      if (photo.gridfs_id) return `${BACKEND_URL}/api/files/gridfs/${photo.gridfs_id}`;
      // Legacy filesystem
      if (photo.path) return `${BACKEND_URL}/api/uploads/${photo.path.split('/').map(p => encodeURIComponent(p)).join('/')}`;
    }
    return `${BACKEND_URL}/api/uploads/${photo}`;
  };

  // Helper to format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Email Proposal to Client
  const handleEmailProposal = () => {
    if (!lead.client_email) {
      toast.error(language === 'es' ? 'No hay email del cliente' : 'No client email available');
      return;
    }
    
    const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
    
    const subject = encodeURIComponent(
      language === 'es' 
        ? `Propuesta de ${COMPANY.name} - ${lead.client_name}`
        : `Proposal from ${COMPANY.name} - ${lead.client_name}`
    );
    
    const body = encodeURIComponent(
      language === 'es'
        ? `Estimado/a ${lead.client_name},\n\nGracias por su interés en nuestros servicios. Adjunto encontrará su propuesta por un total de ${formatCurrency(lead.grand_total)}.\n\nPor favor, visite el siguiente enlace para ver los detalles completos de su propuesta:\n${window.location.href}\n\nSi tiene alguna pregunta, no dude en contactarnos.\n\nAtentamente,\n${COMPANY.name}\n${COMPANY.phone || ''}\n${COMPANY.email || ''}`
        : `Dear ${lead.client_name},\n\nThank you for your interest in our services. Please find attached your proposal for a total of ${formatCurrency(lead.grand_total)}.\n\nPlease visit the following link to view the full details of your proposal:\n${window.location.href}\n\nIf you have any questions, please don't hesitate to contact us.\n\nBest regards,\n${COMPANY.name}\n${COMPANY.phone || ''}\n${COMPANY.email || ''}`
    );
    
    window.open(`mailto:${lead.client_email}?subject=${subject}&body=${body}`, '_blank');
    toast.success(language === 'es' ? 'Abriendo cliente de correo...' : 'Opening email client...');
  };

  // PDF Generation
  const generatePDF = async () => {
    await handleSave();
    setGeneratingPdf(true);
    try {
      const container = estimateRef.current;
      if (!container) {
        toast.error('PDF container not found');
        return;
      }

      // Clone the container and apply print-specific styles
      const printContainer = container.cloneNode(true);
      printContainer.style.width = '612px'; // 8.5 inches at 72dpi
      printContainer.style.padding = '20px';
      printContainer.style.backgroundColor = '#ffffff';
      printContainer.style.position = 'absolute';
      printContainer.style.left = '-9999px';
      printContainer.style.top = '0';
      
      // Apply page break rules to prevent cutting text
      const style = document.createElement('style');
      style.textContent = `
        * { box-sizing: border-box; }
        p, li, h1, h2, h3, h4, h5, h6, div, span, td, th { 
          page-break-inside: avoid !important; 
          break-inside: avoid !important;
          orphans: 3;
          widows: 3;
        }
        table { page-break-inside: auto !important; }
        tr { page-break-inside: avoid !important; break-inside: avoid !important; }
        thead { display: table-header-group; }
        tfoot { display: table-footer-group; }
        .page-break-before { page-break-before: always !important; break-before: page !important; }
        .avoid-break { page-break-inside: avoid !important; break-inside: avoid !important; }
        img { max-width: 100%; height: auto; }
      `;
      printContainer.insertBefore(style, printContainer.firstChild);
      
      // Add avoid-break class to important sections
      printContainer.querySelectorAll('table, .card, [class*="Card"], section').forEach(el => {
        el.classList.add('avoid-break');
      });
      
      document.body.appendChild(printContainer);
      
      // US Letter size: 8.5" x 11" = 215.9mm x 279.4mm
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
      const pdfWidth = 215.9;
      const pdfHeight = 279.4;
      const margin = 8;
      const footerHeight = 6;
      const contentWidth = pdfWidth - (margin * 2);
      const contentHeight = pdfHeight - (margin * 2) - footerHeight;

      // Capture with higher DPI for better text quality
      const canvas = await html2canvas(printContainer, { 
        scale: 3, // Higher scale for sharper text
        useCORS: true, 
        backgroundColor: '#ffffff',
        logging: false,
        allowTaint: true,
        windowWidth: 612,
        windowHeight: printContainer.scrollHeight,
        // Improve text rendering
        letterRendering: true,
        imageTimeout: 0
      });
      
      // Remove temp container
      document.body.removeChild(printContainer);
      
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // If content fits on one page
      if (imgHeight <= contentHeight) {
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, margin, imgWidth, imgHeight);
        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Page 1 of 1`, pdfWidth / 2, pdfHeight - 3, { align: 'center' });
      } else {
        // Multi-page with smart breaks
        const scaleFactor = canvas.width / imgWidth;
        const pageHeightInCanvas = contentHeight * scaleFactor;
        
        // Find natural break points (look for white space / gaps)
        const findNaturalBreak = (startY, maxY) => {
          const ctx = canvas.getContext('2d');
          const searchStart = Math.max(startY, maxY - (50 * scaleFactor)); // Search in last 50mm
          
          for (let y = maxY; y > searchStart; y -= scaleFactor) {
            const imageData = ctx.getImageData(0, y, canvas.width, 2);
            const data = imageData.data;
            
            // Check if this row is mostly white (potential break point)
            let whitePixels = 0;
            for (let i = 0; i < data.length; i += 4) {
              if (data[i] > 250 && data[i+1] > 250 && data[i+2] > 250) {
                whitePixels++;
              }
            }
            
            const whiteRatio = whitePixels / (canvas.width * 2);
            if (whiteRatio > 0.95) { // 95% white = good break point
              return y;
            }
          }
          return maxY; // No good break found, use max
        };
        
        let currentY = 0;
        let pageNum = 0;
        const pages = [];
        
        // Calculate page breaks
        while (currentY < canvas.height) {
          const maxPageEnd = currentY + pageHeightInCanvas;
          let pageEnd;
          
          if (maxPageEnd >= canvas.height) {
            pageEnd = canvas.height;
          } else {
            pageEnd = findNaturalBreak(currentY, maxPageEnd);
          }
          
          pages.push({ start: currentY, end: pageEnd, height: pageEnd - currentY });
          currentY = pageEnd;
        }
        
        // Render pages
        for (let i = 0; i < pages.length; i++) {
          if (i > 0) pdf.addPage();
          
          const page = pages[i];
          const pageCanvas = document.createElement('canvas');
          pageCanvas.width = canvas.width;
          pageCanvas.height = Math.ceil(page.height);
          const ctx = pageCanvas.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
          ctx.drawImage(canvas, 0, page.start, canvas.width, page.height, 0, 0, canvas.width, page.height);
          
          const pageImgHeight = page.height / scaleFactor;
          pdf.addImage(pageCanvas.toDataURL('image/png'), 'PNG', margin, margin, imgWidth, pageImgHeight);
          
          pdf.setFontSize(8);
          pdf.setTextColor(128, 128, 128);
          pdf.text(`Page ${i + 1} of ${pages.length}`, pdfWidth / 2, pdfHeight - 3, { align: 'center' });
        }
      }
      
      // Generate filename
      const clientName = lead.client_name ? lead.client_name.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_') : 'Client';
      const proposalId = lead.proposal_id || lead.estimate_number || '';
      const fileName = `Proposal_${proposalId}_${clientName}.pdf`;
      
      // Save the PDF
      const pdfBlob = pdf.output('blob');
      const blobUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      
      toast.success(language === 'es' ? `PDF guardado: ${fileName}` : `PDF saved: ${fileName}`);
    } catch (error) { 
      console.error('PDF Generation Error:', error); 
      toast.error(language === 'es' ? 'Error al generar PDF' : 'Failed to generate PDF'); 
    } finally { 
      setGeneratingPdf(false); 
    }
  };

  // Generate Word Document
  const generateWordDocument = async () => {
    await handleSave();
    setGeneratingPdf(true);
    try {
      const formatMoney = (amount) => `$${(parseFloat(amount) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      
      // Create document sections
      const children = [];
      
      // Company Header
      children.push(
        new Paragraph({
          children: [new TextRun({ text: companyInfo.name, bold: true, size: 32, color: 'D97706' })],
          alignment: AlignmentType.CENTER,
        }),
        new Paragraph({
          children: [new TextRun({ text: `${getAllLicenses() ? getAllLicenses() + ' • ' : ''}${companyInfo.tagline}`, size: 18, color: '666666' })],
          alignment: AlignmentType.CENTER,
        }),
        new Paragraph({
          children: [new TextRun({ text: `${companyInfo.address}, ${companyInfo.city}, ${companyInfo.state} ${companyInfo.zip}`, size: 18 })],
          alignment: AlignmentType.CENTER,
        }),
        new Paragraph({
          children: [new TextRun({ text: `${companyInfo.phone} | ${companyInfo.email}`, size: 18 })],
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        })
      );

      // ESTIMATE Title
      children.push(
        new Paragraph({
          children: [new TextRun({ text: 'PROPOSAL', bold: true, size: 40 })],
          alignment: AlignmentType.CENTER,
        }),
        new Paragraph({
          children: [new TextRun({ text: lead.proposal_id || lead.estimate_number || '', size: 20, color: 'D97706' })],
          alignment: AlignmentType.CENTER,
        }),
        new Paragraph({
          children: [new TextRun({ text: lead.estimate_date ? new Date(lead.estimate_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '', size: 18 })],
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        })
      );

      // Client & Roof Details Table
      children.push(
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph({ children: [new TextRun({ text: 'CLIENT INFORMATION', bold: true, color: 'FFFFFF', size: 20 })] })],
                  shading: { fill: 'D97706' },
                  width: { size: 50, type: WidthType.PERCENTAGE },
                }),
                new TableCell({
                  children: [new Paragraph({ children: [new TextRun({ text: 'ROOF DETAILS', bold: true, color: 'FFFFFF', size: 20 })] })],
                  shading: { fill: 'D97706' },
                  width: { size: 50, type: WidthType.PERCENTAGE },
                }),
              ],
            }),
            new TableRow({
              children: [
                new TableCell({
                  children: [
                    new Paragraph({ children: [new TextRun({ text: lead.client_name || '—', bold: true, size: 20 })] }),
                    new Paragraph({ children: [new TextRun({ text: lead.client_address || '—', size: 18 })] }),
                    new Paragraph({ children: [new TextRun({ text: [lead.client_city, lead.client_state, lead.client_zip].filter(Boolean).join(', ') || '', size: 18 })] }),
                    new Paragraph({ children: [new TextRun({ text: `${lead.client_phone || ''} | ${lead.client_email || ''}`, size: 18 })] }),
                  ],
                }),
                new TableCell({
                  children: [
                    new Paragraph({ children: [new TextRun({ text: `Proposal #: ${lead.proposal_id || lead.estimate_number || '—'}`, bold: true, size: 18 })] }),
                    new Paragraph({ children: [new TextRun({ text: `Category: ${projectTypeLabels[lead.project_category] || projectTypeLabels[lead.project_type] || '—'}`, size: 18 })] }),
                  ],
                }),
              ],
            }),
          ],
        }),
        new Paragraph({ spacing: { after: 300 } })
      );

      // Introduction Letter (use lead's or settings default)
      const introText = lead.introduction_letter || companyInfo.introductionLetter;
      if (lead.show_intro_letter !== false && introText) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: 'INTRODUCTION', bold: true, size: 24, color: 'D97706' })],
            spacing: { before: 200, after: 200 },
            border: { bottom: { color: 'D97706', size: 6, style: BorderStyle.SINGLE } },
          }),
          new Paragraph({
            children: [new TextRun({ text: `Dear ${lead.client_name || 'Valued Customer'},`, bold: true, size: 22 })],
            spacing: { after: 200 },
          })
        );
        
        // Split intro letter into paragraphs
        const introParagraphs = introText.split('\n\n');
        introParagraphs.forEach(para => {
          if (para.trim()) {
            children.push(new Paragraph({
              children: [new TextRun({ text: para.trim(), size: 20 })],
              spacing: { after: 200 },
            }));
          }
        });
      }

      // Customer Reviews (use lead's or settings default)
      const reviewsText = lead.reviews_content || companyInfo.reviews;
      if (lead.show_reviews !== false && reviewsText) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: 'WHAT OUR CLIENTS SAY', bold: true, size: 24, color: 'D97706' })],
            spacing: { before: 300, after: 200 },
            border: { bottom: { color: 'D97706', size: 6, style: BorderStyle.SINGLE } },
          })
        );
        
        const reviewParagraphs = reviewsText.split('\n\n');
        reviewParagraphs.forEach(para => {
          if (para.trim()) {
            children.push(new Paragraph({
              children: [new TextRun({ text: para.trim(), size: 18 })],
              spacing: { after: 150 },
            }));
          }
        });
      }

      // Page break before line items
      children.push(new Paragraph({ children: [new PageBreak()] }));

      // Check estimate type and render appropriate content
      if (lead.estimate_type === 'lump_sum' && lead.lump_sum_items?.length > 0) {
        // LUMP SUM SCOPE OF WORK
        children.push(
          new Paragraph({
            children: [new TextRun({ text: 'SCOPE OF WORK', bold: true, size: 24, color: 'D97706' })],
            spacing: { before: 200, after: 200 },
            border: { bottom: { color: 'D97706', size: 6, style: BorderStyle.SINGLE } },
          })
        );

        // Add each lump sum item as a bullet point
        lead.lump_sum_items.forEach((item, i) => {
          children.push(
            new Paragraph({
              children: [new TextRun({ text: `${i + 1}. ${item.description || ''}`, size: 20 })],
              spacing: { after: 100 },
            })
          );
        });

        children.push(new Paragraph({ spacing: { after: 200 } }));

        // Lump Sum Total
        children.push(
          new Paragraph({
            children: [new TextRun({ text: `TOTAL: ${formatMoney(lead.lump_sum_total || 0)}`, bold: true, size: 28, color: 'D97706' })],
            alignment: AlignmentType.RIGHT,
            spacing: { before: 200 },
            border: { top: { color: 'D97706', size: 6, style: BorderStyle.SINGLE } },
          })
        );
      } else if (lead.line_items?.length > 0) {
        // ITEMIZED LINE ITEMS
        children.push(
          new Paragraph({
            children: [new TextRun({ text: 'PROPOSAL LINE ITEMS', bold: true, size: 24, color: 'D97706' })],
            spacing: { before: 200, after: 200 },
            border: { bottom: { color: 'D97706', size: 6, style: BorderStyle.SINGLE } },
          })
        );

        const tableRows = [
          new TableRow({
            children: [
              new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'Description', bold: true, size: 18 })] })], shading: { fill: 'F3F4F6' }, width: { size: 50, type: WidthType.PERCENTAGE } }),
              new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'Unit', bold: true, size: 18 })], alignment: AlignmentType.CENTER })], shading: { fill: 'F3F4F6' }, width: { size: 10, type: WidthType.PERCENTAGE } }),
              new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'Qty', bold: true, size: 18 })], alignment: AlignmentType.CENTER })], shading: { fill: 'F3F4F6' }, width: { size: 10, type: WidthType.PERCENTAGE } }),
              new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: '$/Unit', bold: true, size: 18 })], alignment: AlignmentType.RIGHT })], shading: { fill: 'F3F4F6' }, width: { size: 15, type: WidthType.PERCENTAGE } }),
              new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'Total', bold: true, size: 18 })], alignment: AlignmentType.RIGHT })], shading: { fill: 'F3F4F6' }, width: { size: 15, type: WidthType.PERCENTAGE } }),
            ],
          }),
        ];

        lead.line_items.forEach((item, i) => {
          const lineTotal = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
          tableRows.push(
            new TableRow({
              children: [
                new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: item.title || '', size: 18 })] })], shading: i % 2 === 0 ? { fill: 'FEF3C7' } : {} }),
                new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: item.unit || 'SQ', size: 18 })], alignment: AlignmentType.CENTER })], shading: i % 2 === 0 ? { fill: 'FEF3C7' } : {} }),
                new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: String(item.quantity || 0), size: 18 })], alignment: AlignmentType.CENTER })], shading: i % 2 === 0 ? { fill: 'FEF3C7' } : {} }),
                new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: formatMoney(item.unit_price), size: 18 })], alignment: AlignmentType.RIGHT })], shading: i % 2 === 0 ? { fill: 'FEF3C7' } : {} }),
                new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: formatMoney(lineTotal), bold: true, size: 18 })], alignment: AlignmentType.RIGHT })], shading: i % 2 === 0 ? { fill: 'FEF3C7' } : {} }),
              ],
            })
          );
        });

        children.push(
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: tableRows,
          }),
          new Paragraph({ spacing: { after: 300 } })
        );

        // Totals
        children.push(
          new Paragraph({
            children: [new TextRun({ text: `Subtotal: ${formatMoney(totals.subtotal)}`, size: 22 })],
            alignment: AlignmentType.RIGHT,
          })
        );
        
        if (totals.applyOverhead) {
          children.push(
            new Paragraph({
              children: [new TextRun({ text: `Contractor Overhead (10%): ${formatMoney(totals.overheadAmount)}`, size: 22 })],
              alignment: AlignmentType.RIGHT,
            })
          );
        }
        
        if (totals.applyProfit) {
          children.push(
            new Paragraph({
              children: [new TextRun({ text: `Contractor Profit (10%): ${formatMoney(totals.profitAmount)}`, size: 22 })],
              alignment: AlignmentType.RIGHT,
            })
          );
        }
        
        children.push(
          new Paragraph({
            children: [new TextRun({ text: `TOTAL: ${formatMoney(totals.grandTotal)}`, bold: true, size: 28, color: 'D97706' })],
            alignment: AlignmentType.RIGHT,
            spacing: { before: 100, after: 400 },
          })
        );
      }

      // Terms & Conditions on new page (use lead's or settings default)
      const termsText = lead.terms_and_conditions || (language === 'es' ? settings.termsEs : settings.termsEn);
      if (termsText) {
        children.push(
          new Paragraph({ children: [new PageBreak()] }),
          new Paragraph({
            children: [new TextRun({ text: 'TERMS & CONDITIONS', bold: true, size: 24, color: 'D97706' })],
            spacing: { before: 200, after: 200 },
            border: { bottom: { color: 'D97706', size: 6, style: BorderStyle.SINGLE } },
          })
        );
        
        const termsParagraphs = termsText.split('\n\n');
        termsParagraphs.forEach(para => {
          if (para.trim()) {
            children.push(new Paragraph({
              children: [new TextRun({ text: para.trim(), size: 18 })],
              spacing: { after: 150 },
            }));
          }
        });

        // Signature Section
        children.push(
          new Paragraph({ spacing: { before: 600 } }),
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: { top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE }, left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE }, insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE } },
            rows: [
              new TableRow({
                children: [
                  new TableCell({
                    children: [
                      new Paragraph({ children: [new TextRun({ text: 'CONTRACTOR SIGNATURE', bold: true, size: 20 })], spacing: { after: 400 } }),
                      new Paragraph({ children: [new TextRun({ text: '_________________________________', size: 20 })], spacing: { after: 100 } }),
                      new Paragraph({ children: [new TextRun({ text: companyInfo.name, size: 18 })], spacing: { after: 200 } }),
                      new Paragraph({ children: [new TextRun({ text: 'Date: _______________________', size: 18 })] }),
                    ],
                    borders: { top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE }, left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE } },
                  }),
                  new TableCell({
                    children: [
                      new Paragraph({ children: [new TextRun({ text: 'CLIENT SIGNATURE', bold: true, size: 20 })], spacing: { after: 400 } }),
                      new Paragraph({ children: [new TextRun({ text: '_________________________________', size: 20 })], spacing: { after: 100 } }),
                      new Paragraph({ children: [new TextRun({ text: lead.client_name || '(Client Name)', size: 18 })], spacing: { after: 200 } }),
                      new Paragraph({ children: [new TextRun({ text: 'Date: _______________________', size: 18 })] }),
                    ],
                    borders: { top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE }, left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE } },
                  }),
                ],
              }),
            ],
          }),
          new Paragraph({
            children: [new TextRun({ text: 'By signing above, client acknowledges having read and agreed to all terms and conditions.', italics: true, size: 16, color: '666666' })],
            alignment: AlignmentType.CENTER,
            spacing: { before: 400 },
          })
        );
      }

      // Create the document
      const doc = new Document({
        sections: [{
          properties: {
            page: {
              margin: { top: 720, right: 720, bottom: 720, left: 720 },
            },
          },
          headers: {
            default: new Header({
              children: [new Paragraph({ children: [new TextRun({ text: '' })] })],
            }),
          },
          footers: {
            default: new Footer({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({ text: `${companyInfo.website} • ${companyInfo.email} • ${companyInfo.phone}`, size: 16, color: '888888' }),
                    new TextRun({ text: '     Page ' }),
                    new TextRun({ children: [PageNumber.CURRENT] }),
                    new TextRun({ text: ' of ' }),
                    new TextRun({ children: [PageNumber.TOTAL_PAGES] }),
                  ],
                  alignment: AlignmentType.CENTER,
                }),
              ],
            }),
          },
          children: children,
        }],
      });

      // Generate and download
      const blob = await Packer.toBlob(doc);
      const clientName = lead.client_name ? lead.client_name.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_') : 'Estimate';
      const dateStr = new Date().toISOString().split('T')[0];
      const fileName = `Estimate_${clientName}_${dateStr}.docx`;
      
      // Use native download as fallback
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(language === 'es' ? `Word guardado: ${fileName}` : `Word saved: ${fileName}`);
    } catch (error) {
      console.error('Word Generation Error:', error);
      toast.error(language === 'es' ? 'Error al generar Word' : 'Failed to generate Word document');
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Save as Template Function
  const saveAsTemplate = async () => {
    if (!templateName.trim()) {
      toast.error(language === 'es' ? 'Por favor ingrese un nombre para la plantilla' : 'Please enter a template name');
      return;
    }
    
    setSavingTemplate(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/estimate-templates/from-estimate/${leadId}?name=${encodeURIComponent(templateName)}&description=${encodeURIComponent(templateDescription)}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        toast.success(language === 'es' ? '¡Plantilla guardada exitosamente!' : 'Template saved successfully!');
        setShowSaveTemplateModal(false);
        setTemplateName('');
        setTemplateDescription('');
      } else {
        const error = await response.json();
        toast.error(error.detail || (language === 'es' ? 'Error al guardar plantilla' : 'Failed to save template'));
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(language === 'es' ? 'Error al guardar plantilla' : 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  // Open Print Preview - for Google Docs / Print
  const openPrintPreview = async () => {
    await handleSave();
    const formatMoney = (amount) => `$${(parseFloat(amount) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    
    // Build line items HTML - respects itemized vs lump sum estimate type
    let lineItemsHtml = '';
    
    if (lead.estimate_type === 'lump_sum') {
      // LUMP SUM ESTIMATE - Category + Description only, with manual total
      let tableHtml = '';
      if ((lead.lump_sum_items || []).length > 0) {
        tableHtml = `
            <h3 style="color: #D97706; border-bottom: 2px solid #D97706; padding-bottom: 8px; margin-bottom: 15px;">Scope of Work</h3>
            <table style="width: 100%; border-collapse: collapse;">
              <thead>
                <tr style="background-color: #D97706; color: white;">
                  <th style="padding: 10px; text-align: left; border: 1px solid #ddd; width: 35%;">Category</th>
                  <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Description</th>
                </tr>
              </thead>
              <tbody>
                ${(lead.lump_sum_items || []).map((item, i) => `
                  <tr style="background-color: ${i % 2 === 0 ? '#FEF3C7' : 'white'};">
                    <td style="padding: 10px; border: 1px solid #ddd; font-weight: 500;">${item.category || ''}</td>
                    <td style="padding: 10px; border: 1px solid #ddd;">${item.description || ''}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
        `;
      }
      // ALWAYS show total for lump sum estimates
      lineItemsHtml = `
          <div style="margin: 20px 0;">
            ${tableHtml}
            <div style="margin-top: 20px; text-align: right;">
              <div style="display: inline-block; background-color: #D97706; color: white; padding: 15px 30px; border-radius: 8px;">
                <span style="font-size: 1.1em;">TOTAL:</span>
                <span style="font-size: 1.5em; font-weight: bold; margin-left: 15px;">${formatMoney(lead.lump_sum_total || 0)}</span>
              </div>
            </div>
          </div>
        `;
    } else if (lead.line_items?.length > 0) {
      // ITEMIZED ESTIMATE
      if (lead.show_itemized_pricing !== false) {
        // ITEMIZED VIEW - Show all pricing columns
        lineItemsHtml = `
          <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
            <thead>
              <tr style="background-color: #D97706; color: white;">
                <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Description</th>
                <th style="padding: 10px; text-align: center; border: 1px solid #ddd; width: 60px;">Unit</th>
                <th style="padding: 10px; text-align: center; border: 1px solid #ddd; width: 60px;">Qty</th>
                <th style="padding: 10px; text-align: right; border: 1px solid #ddd; width: 80px;">$/Unit</th>
                <th style="padding: 10px; text-align: right; border: 1px solid #ddd; width: 100px;">Total</th>
              </tr>
            </thead>
            <tbody>
              ${lead.line_items.map((item, i) => {
                const lineTotal = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
                return `
                  <tr style="background-color: ${i % 2 === 0 ? '#FEF3C7' : 'white'};">
                    <td style="padding: 8px; border: 1px solid #ddd;">${item.title || ''}</td>
                    <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${item.unit || 'SQ'}</td>
                    <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${item.quantity || 0}</td>
                    <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${formatMoney(item.unit_price)}</td>
                    <td style="padding: 8px; border: 1px solid #ddd; text-align: right; font-weight: bold;">${formatMoney(lineTotal)}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        `;
      } else {
        // NON-ITEMIZED VIEW - Show only descriptions, no pricing columns
        lineItemsHtml = `
          <div style="margin: 20px 0;">
            <h3 style="color: #D97706; border-bottom: 2px solid #D97706; padding-bottom: 8px; margin-bottom: 15px;">Scope of Work</h3>
            <ul style="list-style-type: none; padding: 0; margin: 0;">
              ${lead.line_items.map((item, i) => `
                <li style="padding: 10px 15px; background-color: ${i % 2 === 0 ? '#FEF3C7' : 'white'}; border-left: 3px solid #D97706; margin-bottom: 5px;">
                  <span style="font-weight: 500;">${item.title || ''}</span>
                  ${item.description ? `<br><span style="color: #666; font-size: 0.9em;">${item.description}</span>` : ''}
                </li>
              `).join('')}
            </ul>
          </div>
        `;
      }
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Estimate - ${lead.client_name || 'Client'}</title>
        <style>
          @media print {
            body { margin: 0; padding: 20px; }
            .no-print { display: none !important; }
            .page-break { page-break-before: always; }
          }
          body { 
            font-family: Arial, sans-serif; 
            max-width: 800px; 
            margin: 0 auto; 
            padding: 20px;
            color: #333;
            line-height: 1.5;
          }
          /* Page size and print settings for 8.5x11 */
          @page {
            size: 8.5in 11in;
            margin: 0.5in;
          }
          @media print {
            body { margin: 0; padding: 0; }
            .document { max-width: 100%; padding: 0; }
            .print-btn { display: none !important; }
            .section { page-break-inside: avoid; }
            .signature-grid { page-break-inside: avoid; }
            .totals { page-break-inside: avoid; }
          }
          .header { 
            background-color: #D97706; 
            color: white; 
            padding: 20px; 
            text-align: center;
            margin-bottom: 20px;
          }
          .header h1 { margin: 0 0 5px 0; font-size: 24px; }
          .header p { margin: 3px 0; font-size: 12px; }
          .title { text-align: center; margin: 20px 0; }
          .title h2 { font-size: 28px; margin: 0; }
          .title .proposal-id { color: #D97706; font-family: monospace; font-size: 18px; font-weight: bold; }
          .info-grid { display: flex; gap: 20px; margin: 20px 0; }
          .info-box { flex: 1; border: 1px solid #ddd; }
          .info-box-header { background-color: #D97706; color: white; padding: 8px; font-weight: bold; font-size: 12px; }
          .info-box-content { padding: 10px; font-size: 12px; }
          .section { margin: 25px 0; page-break-inside: avoid; }
          .section-header { 
            background-color: #D97706; 
            color: white; 
            padding: 8px 12px; 
            font-weight: bold;
            font-size: 14px;
            margin-bottom: 0;
          }
          .section-content { 
            padding: 15px; 
            background-color: #f9f9f9; 
            border: 1px solid #ddd;
            border-top: none;
            font-size: 12px;
            white-space: pre-line;
          }
          .totals { text-align: right; margin: 20px 0; page-break-inside: avoid; }
          .totals p { margin: 5px 0; font-size: 14px; }
          .grand-total { 
            background-color: #D97706; 
            color: white; 
            padding: 10px 20px; 
            display: inline-block;
            font-size: 18px;
            font-weight: bold;
          }
          .signature-grid { display: flex; gap: 40px; margin-top: 40px; page-break-inside: avoid; }
          .signature-box { flex: 1; }
          .signature-box h4 { margin-bottom: 30px; font-size: 12px; }
          .signature-line { border-bottom: 1px solid #333; margin-bottom: 5px; height: 30px; }
          .signature-name { font-size: 11px; }
          .footer { text-align: center; margin-top: 30px; padding-top: 15px; border-top: 1px solid #ddd; font-size: 11px; color: #666; }
          .print-btn { 
            position: fixed; 
            top: 20px; 
            right: 20px; 
            background: #D97706; 
            color: white; 
            border: none; 
            padding: 15px 30px; 
            font-size: 16px; 
            cursor: pointer;
            border-radius: 5px;
            box-shadow: 0 2px 5px rgba(0,0,0,0.2);
          }
          .print-btn:hover { background: #b45309; }
        </style>
      </head>
      <body>
        <button class="print-btn no-print" onclick="window.print()">🖨️ Print / Save as PDF</button>
        
        <div class="header">
          <h1>${companyInfo.name}</h1>
          <p>${getAllLicenses() ? getAllLicenses() + ' • ' : ''}${companyInfo.tagline}</p>
          <p>${companyInfo.address}, ${companyInfo.city}, ${companyInfo.state} ${companyInfo.zip}</p>
          <p>${companyInfo.phone} | ${companyInfo.email}</p>
        </div>

        <div class="title">
          <h2>PROPOSAL</h2>
          <p class="proposal-id">${lead.proposal_id || lead.estimate_number || ''}</p>
          <p>${lead.estimate_date ? new Date(lead.estimate_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}</p>
        </div>

        <div class="info-grid">
          <div class="info-box">
            <div class="info-box-header">CLIENT INFORMATION</div>
            <div class="info-box-content">
              <strong>${lead.client_name || '—'}</strong><br>
              ${lead.client_address || '—'}<br>
              ${[lead.client_city, lead.client_state, lead.client_zip].filter(Boolean).join(', ') || ''}<br>
              ${lead.client_phone || ''} | ${lead.client_email || ''}
            </div>
          </div>
          <div class="info-box">
            <div class="info-box-header">PROJECT DETAILS</div>
            <div class="info-box-content">
              <strong>Proposal #:</strong> ${lead.proposal_id || lead.estimate_number || '—'}<br>
              <strong>Category:</strong> ${projectTypeLabels[lead.project_category] || projectTypeLabels[lead.project_type] || '—'}
            </div>
          </div>
        </div>

        ${lead.show_intro_letter !== false && (lead.introduction_letter || companyInfo.introductionLetter) ? `
          <div class="section">
            <div class="section-header">INTRODUCTION</div>
            <div class="section-content">
              <strong>Dear ${lead.client_name || 'Valued Customer'},</strong><br><br>
              ${lead.introduction_letter || companyInfo.introductionLetter}
            </div>
          </div>
        ` : ''}

        ${lead.show_reviews !== false && (lead.reviews_content || companyInfo.reviews) ? `
          <div class="section">
            <div class="section-header">⭐ WHAT OUR CLIENTS SAY</div>
            <div class="section-content" style="background-color: #FEF3C7;">
              ${lead.reviews_content || companyInfo.reviews}
            </div>
          </div>
        ` : ''}

        ${lineItemsHtml ? `
          <div class="section">
            <div class="section-header">${lead.estimate_type === 'lump_sum' ? 'SCOPE OF WORK' : 'PROPOSAL LINE ITEMS'}</div>
            ${lineItemsHtml}
          </div>
        ` : ''}

        <div class="totals">
          <p><strong>Subtotal:</strong> ${formatMoney(totals.subtotal)}</p>
          ${totals.applyOverhead ? `<p><strong>Contractor Overhead (10%):</strong> ${formatMoney(totals.overheadAmount)}</p>` : ''}
          ${totals.applyProfit ? `<p><strong>Contractor Profit (10%):</strong> ${formatMoney(totals.profitAmount)}</p>` : ''}
          <div class="grand-total">TOTAL: ${formatMoney(totals.grandTotal)}</div>
        </div>

        ${(lead.terms_and_conditions || (language === 'es' ? settings.termsEs : settings.termsEn)) ? `
          <div class="page-break"></div>
          <div class="section">
            <div class="section-header">TERMS & CONDITIONS</div>
            <div class="section-content">${lead.terms_and_conditions || (language === 'es' ? settings.termsEs : settings.termsEn)}</div>
          </div>

          <div class="signature-grid">
            <div class="signature-box">
              <h4>CONTRACTOR SIGNATURE</h4>
              <div class="signature-line"></div>
              <p class="signature-name">${companyInfo.name}</p>
              <p style="margin-top: 15px; font-size: 11px;">Date: _______________________</p>
            </div>
            <div class="signature-box">
              <h4>CLIENT SIGNATURE</h4>
              <div class="signature-line"></div>
              <p class="signature-name">${lead.client_name || '(Client Name)'}</p>
              <p style="margin-top: 15px; font-size: 11px;">Date: _______________________</p>
            </div>
          </div>
          <p style="text-align: center; font-size: 10px; color: #666; margin-top: 30px;">
            By signing above, client acknowledges having read and agreed to all terms and conditions.
          </p>
        ` : ''}

        <div class="footer">
          ${companyInfo.website} • ${companyInfo.email} • ${companyInfo.phone}
        </div>
      </body>
      </html>
    `;

    // Open in new window
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    toast.success(language === 'es' ? 'Vista previa abierta' : 'Print preview opened');
  };

  // Change Order PDF Generation
  const generateChangeOrderPDF = async (changeOrderIndex) => {
    await handleSave();
    setGeneratingPdf(true);
    try {
      const container = changeOrderRef.current;
      if (!container) {
        toast.error('Change Order container not found');
        return;
      }

      const canvas = await html2canvas(container, { 
        scale: 2.5, 
        useCORS: true, 
        backgroundColor: '#ffffff',
        logging: false,
        allowTaint: true
      });
      
      // US Letter size
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
      const pdfWidth = 215.9;
      const pdfHeight = 279.4;
      const margin = 12.7;
      const imgWidth = pdfWidth - (margin * 2);
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', margin, margin, imgWidth, Math.min(imgHeight, pdfHeight - margin * 2));
      
      const clientName = lead.client_name ? lead.client_name.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_') : 'ChangeOrder';
      const coNumber = lead.change_orders?.[changeOrderIndex]?.change_order_number || `CO-${changeOrderIndex + 1}`;
      const fileName = `ChangeOrder_${clientName}_${coNumber}.pdf`;
      
      pdf.save(fileName);
      toast.success(language === 'es' ? `Orden de cambio guardada: ${fileName}` : `Change Order saved: ${fileName}`);
    } catch (error) { 
      console.error('Change Order PDF Error:', error); 
      toast.error(language === 'es' ? 'Error al generar orden de cambio' : 'Failed to generate change order'); 
    } finally { 
      setGeneratingPdf(false); 
    }
  };

  // Email PDF function - generates PDF and sends via email
  const emailPdf = async (docType = 'estimate') => {
    if (!lead.client_email) {
      toast.error(language === 'es' ? 'No hay email del cliente' : 'No client email available');
      return;
    }

    await handleSave();
    setGeneratingPdf(true);
    
    try {
      toast.loading(language === 'es' ? 'Generando PDF y enviando...' : 'Generating PDF and sending...');
      
      // Determine which container to use
      let container;
      if (docType === 'invoice') {
        container = invoiceRef.current;
      } else {
        container = estimateRef.current;
      }
      
      if (!container) {
        toast.dismiss();
        toast.error('Document container not found');
        setGeneratingPdf(false);
        return;
      }

      // Generate PDF
      const canvas = await html2canvas(container, { 
        scale: 2.5, 
        useCORS: true, 
        backgroundColor: '#ffffff',
        logging: false,
        allowTaint: true
      });
      
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
      const pdfWidth = 215.9;
      const pdfHeight = 279.4;
      const margin = 12.7;
      const imgWidth = pdfWidth - (margin * 2);
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', margin, margin, imgWidth, Math.min(imgHeight, pdfHeight - margin * 2));
      
      // Convert PDF to base64
      const pdfBase64 = pdf.output('datauristring').split(',')[1];
      
      // Send email with PDF attachment
      const response = await fetch(`${BACKEND_URL}/api/email/send-proposal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lead_id: lead.id,
          recipient_email: lead.client_email.trim(),
          pdf_base64: pdfBase64,
          document_type: docType
        })
      });
      
      const result = await response.json();
      toast.dismiss();
      
      if (result.success) {
        toast.success(language === 'es' 
          ? `¡${docType === 'invoice' ? 'Factura' : 'Estimado'} enviado a ${lead.client_email}!` 
          : `${docType === 'invoice' ? 'Invoice' : 'Estimate'} sent to ${lead.client_email}!`);
      } else {
        toast.error(result.detail || 'Failed to send email');
      }
    } catch (error) {
      toast.dismiss();
      console.error('Email PDF error:', error);
      toast.error(language === 'es' ? 'Error al enviar email' : 'Error sending email');
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Invoice helper functions
  const updateInvoice = (field, value) => {
    setLead(prev => {
      const newInvoice = { ...prev.invoice, [field]: value };
      // Auto-calculate balance due
      if (field === 'amount_paid' || field === 'total_amount') {
        const total = field === 'total_amount' ? parseFloat(value) || 0 : parseFloat(newInvoice.total_amount) || 0;
        const paid = field === 'amount_paid' ? parseFloat(value) || 0 : parseFloat(newInvoice.amount_paid) || 0;
        newInvoice.balance_due = total - paid;
        // Auto-update status
        if (newInvoice.balance_due <= 0 && total > 0) {
          newInvoice.status = 'paid';
        } else if (paid > 0) {
          newInvoice.status = 'partial';
        }
      }
      return { ...prev, invoice: newInvoice };
    });
  };

  const syncInvoiceFromEstimate = () => {
    const totals = calculateTotals();
    setLead(prev => ({
      ...prev,
      invoice: {
        ...prev.invoice,
        total_amount: totals.grandTotal,
        balance_due: totals.grandTotal - (prev.invoice?.amount_paid || 0)
      }
    }));
    toast.success(language === 'es' ? 'Factura sincronizada con presupuesto' : 'Invoice synced with estimate');
  };

  // Change Order helper functions
  const addChangeOrder = () => {
    const newCO = {
      id: crypto.randomUUID(),
      change_order_number: `CO-${(lead.change_orders?.length || 0) + 1}`,
      change_order_date: new Date().toISOString().split('T')[0],
      description: '',
      additional_cost: 0,
      status: 'pending'
    };
    setLead(prev => ({
      ...prev,
      change_orders: [...(prev.change_orders || []), newCO]
    }));
  };

  const updateChangeOrder = (index, field, value) => {
    setLead(prev => {
      const updated = [...(prev.change_orders || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, change_orders: updated };
    });
  };

  const deleteChangeOrder = (index) => {
    setLead(prev => ({
      ...prev,
      change_orders: prev.change_orders.filter((_, i) => i !== index)
    }));
    toast.success(language === 'es' ? 'Orden de cambio eliminada' : 'Change order deleted');
  };

  // Payment terms options
  const paymentTermsOptions = [
    { value: 'due_on_receipt', label: language === 'es' ? 'Pago al Recibir' : 'Due on Receipt' },
    { value: 'net_15', label: 'Net 15' },
    { value: 'net_30', label: 'Net 30' },
    { value: 'net_45', label: 'Net 45' },
    { value: 'net_60', label: 'Net 60' },
    { value: '50_deposit', label: language === 'es' ? '50% Depósito' : '50% Deposit Required' },
    { value: 'due_on_completion', label: language === 'es' ? 'Al Completar' : 'Due on Completion' }
  ];

  // Invoice status options
  const invoiceStatusOptions = [
    { value: 'draft', label: language === 'es' ? 'Borrador' : 'Draft', color: 'bg-gray-100 text-gray-700' },
    { value: 'sent', label: language === 'es' ? 'Enviada' : 'Sent', color: 'bg-blue-100 text-blue-700' },
    { value: 'partial', label: language === 'es' ? 'Pago Parcial' : 'Partial Payment', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'paid', label: language === 'es' ? 'Pagada' : 'Paid', color: 'bg-green-100 text-green-700' },
    { value: 'overdue', label: language === 'es' ? 'Vencida' : 'Overdue', color: 'bg-red-100 text-red-700' }
  ];

  // Change Order status options
  const changeOrderStatusOptions = [
    { value: 'pending', label: language === 'es' ? 'Pendiente' : 'Pending', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'approved', label: language === 'es' ? 'Aprobada' : 'Approved', color: 'bg-green-100 text-green-700' },
    { value: 'rejected', label: language === 'es' ? 'Rechazada' : 'Rejected', color: 'bg-red-100 text-red-700' },
    { value: 'completed', label: language === 'es' ? 'Completada' : 'Completed', color: 'bg-blue-100 text-blue-700' }
  ];

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  const getLeadStatusBadge = (leadStatus) => {
    const config = leadStatusOptions.find(s => s.value === leadStatus) || leadStatusOptions[0];
    return <Badge variant="outline" className={config.color}>{config.label}</Badge>;
  };

  // Calculate totals for display
  const calculateTotals = () => {
    let subtotal = 0;
    let miscTotal = 0;
    let itemsTotal = 0;
    
    if (lead?.estimate_type === 'lump_sum') {
      // For lump sum: use manual lump_sum_total + misc items
      const lumpSumBase = parseFloat(lead?.lump_sum_total) || 0;
      miscTotal = (lead?.misc_items || []).reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);
      subtotal = lumpSumBase + miscTotal;
      itemsTotal = lumpSumBase; // Show lump sum as "items total" for display
    } else {
      // For itemized: calculate from line items + misc items
      miscTotal = (lead?.misc_items || []).reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);
      itemsTotal = (lead?.line_items || []).reduce((sum, item) => {
        const qty = parseFloat(item.quantity) || 0;
        const unitPrice = parseFloat(item.unit_price) || 0;
        return sum + (qty * unitPrice);
      }, 0);
      subtotal = miscTotal + itemsTotal;
    }
    
    // Calculate Contractor Overhead (10%) if enabled
    const applyOverhead = lead?.apply_overhead === true;
    const overheadAmount = applyOverhead ? subtotal * 0.10 : 0;
    
    // Calculate Contractor Profit (10%) if enabled - applied after overhead
    const applyProfit = lead?.apply_profit === true;
    const profitAmount = applyProfit ? (subtotal + overheadAmount) * 0.10 : 0;
    
    const grandTotal = subtotal + overheadAmount + profitAmount;
    return { miscTotal, itemsTotal, subtotal, overheadAmount, profitAmount, grandTotal, applyOverhead, applyProfit };
  };

  // Fuzzy search with Fuse.js for better typo tolerance
  const fuse = useMemo(() => {
    const templatesWithLabels = lineItemTemplates.map(t => ({
      ...t,
      categoryLabel: projectTypeLabels[t.project_type] || t.project_type || 'General'
    }));
    return new Fuse(templatesWithLabels, {
      keys: ['title', 'title_es', 'description', 'description_es', 'categoryLabel', 'project_type'],
      threshold: 0.4, // Allow fuzzy matches (0 = exact, 1 = match anything)
      includeScore: true,
      ignoreLocation: true,
      minMatchCharLength: 2
    });
  }, [lineItemTemplates, projectTypeLabels]);

  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) return lineItemTemplates;
    const results = fuse.search(searchQuery.trim());
    return results.map(r => r.item);
  }, [searchQuery, fuse, lineItemTemplates]);

  // Estimate Image Upload Handler
  const estimateImageInputRef = useRef(null);
  
  const handleEstimateImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Accept both images and PDFs
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      toast.error(language === 'es' ? 'Solo imágenes o PDFs permitidos' : 'Only images or PDFs allowed');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(`${BACKEND_URL}/api/estimates/${leadId}/photos`, {
        method: 'POST',
        body: formData
      });
      if (response.ok) {
        const result = await response.json();
        // Add to estimate_images array with caption
        const newImage = {
          id: crypto.randomUUID(),
          path: result.path,
          caption: file.name.replace(/\.[^/.]+$/, ''), // Remove extension for caption
          type: file.type.startsWith('image/') ? 'image' : 'pdf'
        };
        setLead(prev => ({ 
          ...prev, 
          estimate_images: [...(prev.estimate_images || []), newImage]
        }));
        toast.success(language === 'es' ? 'Imagen agregada' : 'Image added');
      }
    } catch (error) { 
      console.error('Error uploading image:', error);
      toast.error(language === 'es' ? 'Error al subir imagen' : 'Error uploading image');
    }
  };

  const removeEstimateImage = async (imageId, imagePath) => {
    // Remove from local state
    setLead(prev => ({
      ...prev,
      estimate_images: prev.estimate_images.filter(img => img.id !== imageId)
    }));
    
    // Try to delete from server
    if (imagePath) {
      const filename = imagePath.split('/').pop();
      try {
        await fetch(`${BACKEND_URL}/api/estimates/${leadId}/photos/${filename}`, { method: 'DELETE' });
      } catch (error) { console.error('Error deleting image:', error); }
    }
    toast.success(language === 'es' ? 'Imagen eliminada' : 'Image removed');
  };

  const updateEstimateImageCaption = (imageId, newCaption) => {
    setLead(prev => ({
      ...prev,
      estimate_images: prev.estimate_images.map(img => 
        img.id === imageId ? { ...img, caption: newCaption } : img
      )
    }));
  };

  // Tabs reorganized per user request - Simplified to 4 tabs + Project + Accounting
  // Tabs reorganized per user request - Accounting moved to Client page
  const tabs = [
    { id: 'overview', label: language === 'es' ? 'Resumen' : 'Overview', icon: User },
    { id: 'proposal', label: language === 'es' ? 'Propuesta' : 'Proposal', icon: FileText },
    { id: 'project', label: language === 'es' ? 'Proyecto' : 'Project', icon: Building },
    { id: 'contacts', label: language === 'es' ? 'Contactos' : 'Contacts', icon: Users, count: contacts.length },
    { id: 'files', label: language === 'es' ? 'Archivos' : 'Files', icon: FolderOpen, count: (lead?.documents?.length || 0) + (lead?.photos?.length || 0) },
    { id: 'notes', label: language === 'es' ? 'Notas' : 'Notes', icon: StickyNote, count: lead?.notes?.length },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!lead) return null;

  const totals = calculateTotals();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-amber-50">
      {/* Header - Extracted Component */}
      <LeadDetailHeader
        language={language}
        lead={lead}
        prevLead={prevLead}
        nextLead={nextLead}
        currentLeadIndex={currentLeadIndex}
        allLeads={allLeads}
        navigateToLead={navigateToLead}
        getLeadStatusBadge={getLeadStatusBadge}
        generateWordDocument={generateWordDocument}
        generatePDF={generatePDF}
        handleEmailProposal={handleEmailProposal}
        handleSave={handleSave}
        generatingPdf={generatingPdf}
        saving={saving}
      />

      {/* Save as Template Modal - Extracted Component */}
      <SaveTemplateModal
        language={language}
        showModal={showSaveTemplateModal}
        setShowModal={setShowSaveTemplateModal}
        templateName={templateName}
        setTemplateName={setTemplateName}
        templateDescription={templateDescription}
        setTemplateDescription={setTemplateDescription}
        saveAsTemplate={saveAsTemplate}
        savingTemplate={savingTemplate}
      />

      <main className="container mx-auto px-4 sm:px-6 py-6">
        {/* Tabs - Extracted Component */}
        <LeadTabNavigation tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Form */}
          <div className="space-y-6">
            {/* Overview Tab - Consolidated Contact & Project Info */}
            {activeTab === 'overview' && (
              <>
                {/* Contact Information Card - Extracted Component */}
                <ContactInformationCard
                  language={language}
                  lead={lead}
                  handleInputChange={handleInputChange}
                />

                {/* Project Measurements Card - Extracted Component */}
                <ProjectMeasurementsCard
                  language={language}
                  lead={lead}
                  handleInputChange={handleInputChange}
                />

                {/* Quick Actions - Extracted Component */}
                <QuickActionsCard
                  language={language}
                  lead={lead}
                  setActiveTab={setActiveTab}
                />
              </>
            )}


            {/* Insurance Tab */}

            {/* Project Tab - Extracted Component */}
            {activeTab === 'project' && (
              <LeadProjectTab
                language={language}
                lead={lead}
                handleInputChange={handleInputChange}
                newProjectNote={newProjectNote}
                setNewProjectNote={setNewProjectNote}
                addProjectNote={addProjectNote}
                deleteProjectNote={deleteProjectNote}
              />
            )}

            {/* Contacts Tab */}
            {/* Contacts Tab - Extracted Component */}
            {activeTab === 'contacts' && (
              <LeadContactsTab
                language={language}
                contacts={contacts}
                openContactModal={openContactModal}
                deleteContact={deleteContact}
              />
            )}

            {/* Estimate Tab */}
            {activeTab === 'proposal' && (
              <>
                {/* AI Estimate Generator Section - Extracted Component */}
                <AIEstimateGeneratorCard
                  language={language}
                  lead={lead}
                  handleInputChange={handleInputChange}
                  // AI State
                  aiEstimateType={aiEstimateType}
                  setAiEstimateType={setAiEstimateType}
                  aiProjectCategory={aiProjectCategory}
                  setAiProjectCategory={setAiProjectCategory}
                  aiQualityTier={aiQualityTier}
                  setAiQualityTier={setAiQualityTier}
                  aiRegion={aiRegion}
                  setAiRegion={setAiRegion}
                  aiRoofType={aiRoofType}
                  setAiRoofType={setAiRoofType}
                  aiMeasurements={aiMeasurements}
                  setAiMeasurements={setAiMeasurements}
                  aiPrompt={aiPrompt}
                  setAiPrompt={setAiPrompt}
                  aiGenerating={aiGenerating}
                  aiGeneratedEstimate={aiGeneratedEstimate}
                  handleGenerateAIEstimate={handleGenerateAIEstimate}
                  applyAIEstimateToProposal={applyAIEstimateToProposal}
                  // Voice
                  isRecording={isRecording}
                  startRecording={startRecording}
                  stopRecording={stopRecording}
                  // Email Writer
                  showEmailWriter={showEmailWriter}
                  setShowEmailWriter={setShowEmailWriter}
                  emailType={emailType}
                  setEmailType={setEmailType}
                  customEmailContext={customEmailContext}
                  setCustomEmailContext={setCustomEmailContext}
                  emailGenerating={emailGenerating}
                  generatedEmail={generatedEmail}
                  setGeneratedEmail={setGeneratedEmail}
                  handleGenerateEmail={handleGenerateEmail}
                  handleSendGeneratedEmail={handleSendGeneratedEmail}
                />


                {/* Insurance Information Section - Extracted Component */}
                <InsuranceInfoSection
                  language={language}
                  lead={lead}
                  handleInputChange={handleInputChange}
                />

                {/* Estimate Type Selector - Extracted Component */}
                <EstimateTypeSelectorCard
                  language={language}
                  lead={lead}
                  setLead={setLead}
                  handleInputChange={handleInputChange}
                  setAiEstimateType={setAiEstimateType}
                  setAiGeneratedEstimate={setAiGeneratedEstimate}
                  setAiItemizedEstimate={setAiItemizedEstimate}
                />

                {/* AI Smart Pricing Suggestions */}
                <SmartPricingSuggestions 
                  projectType={lead.project_category || lead.project_type}
                  county={lead.county}
                  squareFootage={lead.square_footage}
                  onApplyPrice={(price) => {
                    // Apply suggested price to lump sum if using lump sum mode
                    if (lead.estimate_type === 'lump_sum') {
                      handleInputChange('lump_sum_total', price);
                      toast.success(language === 'es' ? 'Precio sugerido aplicado' : 'Suggested price applied');
                    }
                  }}
                />

                {/* ITEMIZED ESTIMATE - Line Items with pricing - Extracted Component */}
                {lead.estimate_type !== 'lump_sum' && (
                  <EstimateLineItemsSection
                    language={language}
                    lead={lead}
                    lineItems={lead.line_items || []}
                    showTemplateSearch={showTemplateSearch}
                    setShowTemplateSearch={setShowTemplateSearch}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    filteredTemplates={filteredTemplates}
                    projectTypeLabels={projectTypeLabels}
                    unitTypes={unitTypes}
                    handleInputChange={handleInputChange}
                    handleLineItemChange={handleLineItemChange}
                    addLineItem={addLineItem}
                    addCategoryCluster={addCategoryCluster}
                    removeLineItem={removeLineItem}
                    moveLineItemUp={moveLineItemUp}
                    moveLineItemDown={moveLineItemDown}
                    fetchLineItemTemplates={fetchLineItemTemplates}
                    formatCurrency={formatCurrency}
                  />
                )}

                {/* LUMP SUM ESTIMATE - Extracted Component */}
                {lead.estimate_type === 'lump_sum' && (
                  <EstimateLumpSumSection
                    language={language}
                    lead={lead}
                    lumpSumItems={lead.lump_sum_items || []}
                    showLumpSumLibrary={showLumpSumLibrary}
                    setShowLumpSumLibrary={setShowLumpSumLibrary}
                    lumpSumSearchQuery={lumpSumSearchQuery}
                    setLumpSumSearchQuery={setLumpSumSearchQuery}
                    filteredLumpSumTemplates={filteredLumpSumTemplates}
                    handleInputChange={handleInputChange}
                    handleLumpSumItemChange={handleLumpSumItemChange}
                    addLumpSumItem={addLumpSumItem}
                    addAllLumpSumFromCategory={addAllLumpSumFromCategory}
                    removeLumpSumItem={removeLumpSumItem}
                    fetchLumpSumTemplates={fetchLumpSumTemplates}
                  />
                )}

                {/* Document Sections - Extracted Component */}
                <EstimateDocumentSections
                  language={language}
                  lead={lead}
                  settings={settings}
                  handleInputChange={handleInputChange}
                  addMiscItem={addMiscItem}
                  handleMiscItemChange={handleMiscItemChange}
                  removeMiscItem={removeMiscItem}
                />

                {/* Save Button at Bottom of Estimate */}
                <div className="flex justify-center gap-4 mt-6 flex-wrap">
                  <Button 
                    onClick={handleSave} 
                    size="lg"
                    style={{ backgroundColor: COMPANY.primaryColor }} 
                    className="text-white px-8" 
                    disabled={saving}
                  >
                    <Save className="mr-2 h-5 w-5" />
                    {saving ? (language === 'es' ? 'Guardando...' : 'Saving...') : (language === 'es' ? 'Guardar Propuesta' : 'Save Proposal')}
                  </Button>
                  <Button 
                    onClick={() => setShowSaveTemplateModal(true)} 
                    size="lg"
                    variant="outline" 
                    className="border-purple-600 text-purple-600 hover:bg-purple-50 px-8" 
                    disabled={!lead.line_items || lead.line_items.length === 0}
                    data-testid="open-save-template-modal"
                  >
                    <Copy className="mr-2 h-5 w-5" />
                    {language === 'es' ? 'Guardar como Plantilla' : 'Save as Template'}
                  </Button>
                  <Button 
                    onClick={generateWordDocument} 
                    size="lg"
                    variant="outline" 
                    className="border-blue-600 text-blue-600 hover:bg-blue-50 px-8" 
                    disabled={generatingPdf}
                  >
                    <FileDown className="mr-2 h-5 w-5" />
                    {language === 'es' ? 'Word' : 'Word'}
                  </Button>
                  <Button 
                    onClick={generatePDF} 
                    size="lg"
                    variant="outline" 
                    className="border-amber-600 text-amber-600 hover:bg-amber-50 px-8" 
                    disabled={generatingPdf}
                  >
                    <Download className="mr-2 h-5 w-5" />
                    {language === 'es' ? 'PDF' : 'PDF'}
                  </Button>
                </div>
              </>
            )}

            {/* Notes Tab - Extracted Component */}
            {activeTab === 'notes' && (
              <LeadNotesTab
                language={language}
                lead={lead}
                newNote={newNote}
                setNewNote={setNewNote}
                addNote={addNote}
                deleteNote={deleteNote}
              />
            )}

            {/* Files Tab - Extracted Component */}
            {activeTab === 'files' && (
              <LeadFilesTab
                language={language}
                lead={lead}
                handleInputChange={handleInputChange}
                fileInputRef={fileInputRef}
                handleDocumentUpload={handleDocumentUpload}
                getDocumentName={getDocumentName}
                getDocumentUrl={getDocumentUrl}
                formatFileSize={formatFileSize}
                deleteDocument={deleteDocument}
                photoInputRef={photoInputRef}
                handlePhotoUpload={handlePhotoUpload}
                getPhotoUrl={getPhotoUrl}
                deletePhoto={deletePhoto}
              />
            )}

            {/* Invoice Tab - Extracted Component */}
            {activeTab === 'invoice_hidden' && (
              <LeadInvoiceTab
                lead={lead}
                language={language}
                companyInfo={companyInfo}
                invoiceStatusOptions={invoiceStatusOptions}
                paymentTermsOptions={paymentTermsOptions}
                formatCurrency={formatCurrency}
                generatingPdf={generatingPdf}
                invoiceRef={invoiceRef}
                updateInvoice={updateInvoice}
                syncInvoiceFromEstimate={syncInvoiceFromEstimate}
                generateInvoicePDF={generateInvoicePDF}
                emailPdf={emailPdf}
                getAllLicenses={getAllLicenses}
              />
            )}

            {/* Change Order Tab - Extracted Component */}
            {activeTab === 'changeorder' && (
              <LeadChangeOrderTab
                lead={lead}
                language={language}
                companyInfo={companyInfo}
                changeOrderStatusOptions={changeOrderStatusOptions}
                roofTypes={roofTypes}
                totals={totals}
                formatCurrency={formatCurrency}
                generatingPdf={generatingPdf}
                changeOrderRef={changeOrderRef}
                addChangeOrder={addChangeOrder}
                updateChangeOrder={updateChangeOrder}
                deleteChangeOrder={deleteChangeOrder}
                generateChangeOrderPDF={generateChangeOrderPDF}
                getAllLicenses={getAllLicenses}
              />
            )}
          </div>

          {/* Right Column - PDF Preview */}
          <div className="lg:sticky lg:top-24 lg:h-fit">
            <ProposalPreviewCard
              lead={lead}
              companyInfo={companyInfo}
              settings={settings}
              totals={totals}
              language={language}
              projectTypeLabels={projectTypeLabels}
              estimateRef={estimateRef}
              emailPdf={emailPdf}
              generatingPdf={generatingPdf}
              getAllLicenses={getAllLicenses}
              formatCurrency={formatCurrency}
              getPhotoUrl={getPhotoUrl}
              getPhotoFilename={getPhotoFilename}
            />
          </div>
        </div>
      </main>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle>
                  {editingContact 
                    ? (language === 'es' ? 'Editar Contacto' : 'Edit Contact')
                    : (language === 'es' ? 'Agregar Contacto' : 'Add Contact')
                  }
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={closeContactModal}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{language === 'es' ? 'Nombre' : 'Name'} *</Label>
                <Input
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  placeholder={language === 'es' ? 'Nombre completo' : 'Full name'}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>{language === 'es' ? 'Rol' : 'Role'}</Label>
                <Select value={contactForm.role} onValueChange={(v) => setContactForm({ ...contactForm, role: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CONTACT_ROLES).map(([key, role]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: role.color }} />
                          {language === 'es' ? role.labelEs : role.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>{language === 'es' ? 'Empresa' : 'Company'}</Label>
                <Input
                  value={contactForm.company}
                  onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                  placeholder={language === 'es' ? 'Nombre de empresa' : 'Company name'}
                  className="mt-1"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{language === 'es' ? 'Teléfono' : 'Phone'}</Label>
                  <Input
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>{language === 'es' ? 'Móvil' : 'Mobile'}</Label>
                  <Input
                    value={contactForm.mobile}
                    onChange={(e) => setContactForm({ ...contactForm, mobile: e.target.value })}
                    placeholder="(555) 987-6543"
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  placeholder="email@example.com"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>{language === 'es' ? 'Notas' : 'Notes'}</Label>
                <Textarea
                  value={contactForm.notes}
                  onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })}
                  placeholder={language === 'es' ? 'Notas adicionales...' : 'Additional notes...'}
                  className="mt-1"
                  rows={2}
                />
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={closeContactModal}>
                  {language === 'es' ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button 
                  onClick={saveContact}
                  disabled={!contactForm.name.trim()}
                  style={{ backgroundColor: COMPANY.primaryColor }}
                  className="text-white"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {language === 'es' ? 'Guardar' : 'Save'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

    </div>
  );
};

export default LeadDetail;
